import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  BedDouble,
  Home,
  Users,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Wrench,
  ShieldOff,
  Sparkles,
  Search,
  CalendarDays,
  Accessibility,
  Building2,
  User,
  MapPin,
  Plane,
  Hotel,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Repeat2,
  UserMinus,
  SlidersHorizontal,
  UserPlus,
  ClipboardList,
  X,
} from "lucide-react";
import { TextareaUI } from "../../ui/textareaUI";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { toast } from "sonner";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { getVenues, VenueApi } from "../../services/propertyDetailsService";
import {
  allocateAccommodationRoom,
  AccommodationDashboardDataApi,
  AccommodationRoomTypeOptionApi,
  autoAssignAccommodationRooms,
  AvailableAccommodationRoomApi,
  AvailableAccommodationPropertyApi,
  getAccommodationRoomTypesByProperty,
  getAccommodationDashboard,
  getAvailableAccommodationProperties,
  getAvailableAccommodationRooms,
  ReallocateAccommodationRoomPayload,
  reallocateAccommodationRoom,
  unallocateAccommodationRoom,
  getPropertyTypesReallocate,
  PropertyTypeApi,
} from "../../services/accommodationService";
import { FullScreenLoading } from "components/ui/loader";

/* ─── Types ──────────────────────────────────────────────────────────── */
type TourType = "instant" | "miqaat" | "faiz" | "hotel";
type RoomStatus =
  | "available"
  | "partial"
  | "occupied"
  | "maintenance"
  | "blocked";
type RoomType = string;

interface ServiceRequest {
  type:
    | "wheelchair"
    | "ground-floor"
    | "medical"
    | "special-meal"
    | "extra-bed"
    | "mehram";
  label: string;
}

interface Applicant {
  id: string;
  name: string;
  itsId: string;
  age: number;
  gender: "Male" | "Female";
  isHof?: boolean;
  roomAssigned?: string;
  tourName?: string;
  tourOperator?: string;
  photo?: string;
  vatan?: string;
  category?: string;
  applicantType?: string;
  serviceRequests: ServiceRequest[];
}

interface ApprovedApplicant extends Applicant {
  tourId: string;
  tourName: string;
  tourOperator: string;
  approvedOn: string;
}

interface Family {
  id: string;
  familyCode: string;
  hofItsId: string;
  hofName: string;
  members: Applicant[];
  colorKey: string;
}

interface Room {
  id: string;
  number: string;
  floor: number;
  type: RoomType;
  propertyRoomTypeId?: number;
  roomTypePublicId?: string;
  capacity: number;
  occupied: number;
  status: RoomStatus;
  familyId?: string;
  occupants?: Applicant[];
  partialRate?: boolean;
  arrivalDate?: string;
  departureDate?: string;
  housekeepingDone?: boolean;
  laundryDone?: boolean;
  nightlyRate: number;
  additionalCharges?: number;
  discount?: number;
}

interface Tour {
  id: string;
  name: string;
  tourCode?: string;
  tourType: TourType;
  startDate: string;
  endDate: string;
  propertyId: string;
  specialAccommodationRequest?: string;
  clubbedFamilies: Family[];
  nonClubbedApplicants: Applicant[];
}

interface Property {
  id: string;
  name: string;
  location: string;
  tourType: TourType;
  totalRooms: number;
  totalPax: number;
  icon: string;
  rooms: Room[];
}

interface TourRoomRequest {
  id: string;
  roomAllocationPublicId: string;
  tourId: string;
  propertyId: string;
  venuePublicId: string;
  venue: string;
  roomType: RoomType;
  propertyRoomTypeId?: number;
  roomsRequested: number;
  applicantIds: string[];
  requestedBy: string;
  requestedOn: string;
  checkInDate: string;
  checkOutDate: string;
  status?: string;
  serviceCount?: number;
  assignedRoomNumber?: string;
  assignedPropertyId?: string;
  assignedPropertyRoomTypeId?: number;
}

interface AllocatedRoomRequestSlot {
  request: TourRoomRequest;
  roomIndex: number;
}

type DragAssignment = {
  kind: "family" | "applicant" | "group";
  tourId: string;
  familyId?: string;
  applicantId?: string;
  applicantIds?: string[];
  requestId?: string;
  roomIndex?: number;
};

const requestSlotKey = (requestId: string, roomIndex: number) =>
  `${requestId}:${roomIndex}`;

const setRoomRequestDragPreview = (
  event: React.DragEvent<HTMLElement>,
  roomLabel: string,
  roomType: string,
  applicantCount: number,
) => {
  const preview = document.createElement("div");
  preview.textContent = `${roomLabel} · ${roomType} · ${applicantCount} applicant${applicantCount === 1 ? "" : "s"}`;
  preview.setAttribute("aria-hidden", "true");
  Object.assign(preview.style, {
    position: "fixed",
    top: "-1000px",
    left: "-1000px",
    zIndex: "9999",
    maxWidth: "300px",
    padding: "8px 12px",
    border: "2px solid #0284c7",
    borderRadius: "8px",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "16px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.18)",
  });
  document.body.appendChild(preview);
  event.dataTransfer.setDragImage(preview, 18, 18);
  window.setTimeout(() => preview.remove(), 0);
};

const passengerTone = (age: number) =>
  age < 2
    ? "border-amber-400 bg-amber-100 text-amber-900"
    : age < 12
      ? "border-blue-400 bg-blue-100 text-blue-900"
      : age >= 60
        ? "border-pink-400 bg-pink-100 text-pink-900"
        : "border-emerald-400 bg-emerald-100 text-emerald-900";

const standardRoomType = (capacity: number) =>
  capacity <= 2 ? "Standard 2P" : capacity <= 4 ? "Standard 4P" : "Standard 8P";

const normalizedRoomType = (roomType: string) =>
  roomType
    .toLowerCase()
    .replace(/\broom\b/g, "")
    .replace(/[^a-z0-9]/g, "");

//SHOW ROOM ALLOCATION DRAG API ERROR MSGS FUNCTION
const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }

  const data: unknown = error.response?.data;
  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const response = data as Record<string, unknown>;
    for (const key of ["message", "error", "detail", "title"]) {
      const value = response[key];
      if (typeof value === "string" && value.trim()) return value;
    }

    const validationErrors = response.errors;
    if (Array.isArray(validationErrors)) {
      const messages = validationErrors.filter(
        (value): value is string => typeof value === "string" && !!value.trim(),
      );
      if (messages.length) return messages.join(", ");
    }
    if (validationErrors && typeof validationErrors === "object") {
      const messages = Object.values(validationErrors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter(
          (value): value is string =>
            typeof value === "string" && !!value.trim(),
        );
      if (messages.length) return messages.join(", ");
    }
  }

  return error.message || fallback;
};

/* ─── Color palettes per family ───────────────────────────────────────── */
const PALETTES: Record<
  string,
  { bg: string; text: string; border: string; face: string; pill: string }
> = {
  orange: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-300",
    face: "bg-orange-400",
    pill: "bg-orange-100",
  },
  violet: {
    bg: "bg-violet-50",
    text: "text-violet-700",
    border: "border-violet-300",
    face: "bg-violet-400",
    pill: "bg-violet-100",
  },
  sky: {
    bg: "bg-sky-50",
    text: "text-sky-700",
    border: "border-sky-300",
    face: "bg-sky-400",
    pill: "bg-sky-100",
  },
  rose: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-300",
    face: "bg-rose-400",
    pill: "bg-rose-100",
  },
  teal: {
    bg: "bg-teal-50",
    text: "text-teal-700",
    border: "border-teal-300",
    face: "bg-teal-400",
    pill: "bg-teal-100",
  },
  amber: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-300",
    face: "bg-amber-400",
    pill: "bg-amber-100",
  },
  lime: {
    bg: "bg-lime-50",
    text: "text-lime-700",
    border: "border-lime-300",
    face: "bg-lime-400",
    pill: "bg-lime-100",
  },
  pink: {
    bg: "bg-pink-50",
    text: "text-pink-700",
    border: "border-pink-300",
    face: "bg-pink-400",
    pill: "bg-pink-100",
  },
};
const PALETTE_KEYS = Object.keys(PALETTES);
const pal = (idx: number) => PALETTES[PALETTE_KEYS[idx % PALETTE_KEYS.length]];

/* ─── Config maps ─────────────────────────────────────────────────────── */
const TOUR_CFG: Record<
  TourType,
  {
    label: string;
    bg: string;
    text: string;
    border: string;
    icon: React.ReactNode;
  }
> = {
  instant: {
    label: "Instant",
    bg: "bg-cyan-100",
    text: "text-cyan-700",
    border: "border-cyan-300",
    icon: <Plane className="h-3 w-3" />,
  },
  miqaat: {
    label: "Miqaat",
    bg: "bg-purple-100",
    text: "text-purple-700",
    border: "border-purple-300",
    icon: <Building2 className="h-3 w-3" />,
  },
  faiz: {
    label: "Faiz",
    bg: "bg-amber-100",
    text: "text-amber-700",
    border: "border-amber-300",
    icon: <Home className="h-3 w-3" />,
  },
  hotel: {
    label: "Hotel",
    bg: "bg-blue-100",
    text: "text-blue-700",
    border: "border-blue-300",
    icon: <Hotel className="h-3 w-3" />,
  },
};

const ROOM_TYPE_CFG: Record<
  RoomType,
  { bg: string; text: string; border: string }
> = {
  "Shared-Gents": {
    bg: "bg-blue-100",
    text: "text-blue-700",
    border: "border-blue-300",
  },
  "Shared-Ladies": {
    bg: "bg-pink-100",
    text: "text-pink-700",
    border: "border-pink-300",
  },
  Private: {
    bg: "bg-green-100",
    text: "text-green-700",
    border: "border-green-300",
  },
  Individual: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    border: "border-amber-300",
  },
  Suite: {
    bg: "bg-violet-100",
    text: "text-violet-700",
    border: "border-violet-300",
  },
};

const roomTypeTone = (roomType: string) =>
  ROOM_TYPE_CFG[roomType] ||
  (roomType.toLowerCase().includes("4p")
    ? {
        bg: "bg-pink-100",
        text: "text-pink-700",
        border: "border-pink-300",
      }
    : {
        bg: "bg-blue-100",
        text: "text-blue-700",
        border: "border-blue-300",
      });

const SERVICE_CFG: Record<string, { icon: React.ReactNode; color: string }> = {
  wheelchair: {
    icon: <Accessibility className="h-2.5 w-2.5" />,
    color: "bg-orange-100 text-orange-700 border-orange-300",
  },
  "ground-floor": {
    icon: <Home className="h-2.5 w-2.5" />,
    color: "bg-blue-100 text-blue-700 border-blue-300",
  },
  medical: {
    icon: <AlertTriangle className="h-2.5 w-2.5" />,
    color: "bg-red-100 text-red-700 border-red-300",
  },
  "special-meal": {
    icon: <Sparkles className="h-2.5 w-2.5" />,
    color: "bg-purple-100 text-purple-700 border-purple-300",
  },
  "extra-bed": {
    icon: <BedDouble className="h-2.5 w-2.5" />,
    color: "bg-teal-100 text-teal-700 border-teal-300",
  },
  mehram: {
    icon: <User className="h-2.5 w-2.5" />,
    color: "bg-amber-100 text-amber-700 border-amber-300",
  },
};

const fmt = (d?: string) => {
  if (!d) return "";
  const [y, m, day] = d.slice(0, 10).split("-");
  return `${day}/${m}/${y.slice(2)}`;
};

const TODAY = new Date().toLocaleDateString("en-CA");
// Temporary local QA switch: keep seeded tours and room inventory visible for every date.
const TEST_DATA_ALWAYS_AVAILABLE = true;
const MANUAL_UNASSIGNED_TOUR_ID = "MANUAL-UNASSIGNED";

const apiTourType = (categoryName: string): TourType => {
  const value = categoryName.toLowerCase();
  if (value.includes("miqaat")) return "miqaat";
  if (value.includes("instant")) return "instant";
  if (value.includes("hotel")) return "hotel";
  return "faiz";
};

const apiServiceRequests = (request?: string): ServiceRequest[] => {
  if (!request?.trim()) return [];
  const normalized = request.toLowerCase();
  const type: ServiceRequest["type"] = normalized.includes("wheelchair")
    ? "wheelchair"
    : normalized.includes("ground")
      ? "ground-floor"
      : normalized.includes("meal")
        ? "special-meal"
        : normalized.includes("bed")
          ? "extra-bed"
          : normalized.includes("mehram")
            ? "mehram"
            : "medical";
  return [{ type, label: request }];
};

const allocationRoomNumber = (allocations: Array<Record<string, unknown>>) => {
  const allocation = allocations[0];
  if (!allocation) return undefined;
  const value =
    allocation.roomNumber ||
    allocation.roomName ||
    allocation.propertyRoomName ||
    allocation.allocatedRoomNumber;
  return typeof value === "string" || typeof value === "number"
    ? `#${String(value).replace(/^#/, "")}`
    : undefined;
};

const roomNumberKey = (value?: string | number) =>
  value === undefined
    ? ""
    : String(value).trim().replace(/^#+/, "").toLowerCase();

const availableRoomOptionValue = (room: AvailableAccommodationRoomApi) =>
  `${room.roomPublicId}::${roomNumberKey(room.roomNumber)}`;

const mapAccommodationDashboard = (data: AccommodationDashboardDataApi) => {
  const tours: Tour[] = data.tourRoomRequests.map((tour) => {
    const applicants = new Map<string, Applicant>();
    const apiApplicants = [
      ...tour.applicants,
      ...tour.roomRequests.flatMap((stay) =>
        stay.roomRequests.flatMap((request) => request.applicants),
      ),
    ];
    apiApplicants.forEach((applicant) => {
      applicants.set(applicant.publicId, {
        id: applicant.publicId,
        name: applicant.applicantName,
        itsId: applicant.itsNo,
        age: Number(applicant?.age),
        gender: applicant.gender.toLowerCase() === "female" ? "Female" : "Male",
        applicantType: applicant.applicantType,
        photo: applicant.photoUrl || applicant.photo,
        vatan: applicant.vatan || "N/A",
        category: applicant.category || "N/A",
        tourName: tour.tourName,
        tourOperator: tour.tourOperatorOfficeName || tour.tourOperatorName,
        serviceRequests: apiServiceRequests(tour.specialAccommodationRequest),
      });
    });
    return {
      id: tour.tourPublicId,
      name: tour.tourName,
      tourCode: tour.tourCode,
      tourType: apiTourType(tour.tourCategoryName),
      startDate: tour.arrivalDate.slice(0, 10),
      endDate: tour.departureDate.slice(0, 10),
      propertyId: tour.roomRequests[0]?.propertyPublicId || "",
      specialAccommodationRequest: tour.specialAccommodationRequest,
      clubbedFamilies: [],
      nonClubbedApplicants: Array.from(applicants.values()),
    };
  });

  const roomRequests: TourRoomRequest[] = data.tourRoomRequests.flatMap(
    (tour) =>
      tour.roomRequests.flatMap((stay) =>
        stay.roomRequests.map((request) => ({
          id:
            request.roomAllocationPublicId ||
            `${request.roomSelectionPublicId}-${request.roomAllocationId}`,
          tourId: tour.tourPublicId,
          roomAllocationPublicId: request.roomAllocationPublicId,
          propertyId: request.propertyPublicId,
          venuePublicId: request.venuePublicId || stay.venuePublicId,
          venue: request.venueName,
          roomType: request.roomTypeName,
          propertyRoomTypeId: request.propertyRoomTypeId,
          roomsRequested: 1,
          applicantIds: request.applicants.map(
            (applicant) => applicant.publicId,
          ),
          requestedBy:
            tour.tourOperatorOfficeName ||
            tour.tourOperatorName ||
            "Tour Operator",
          requestedOn: "",
          checkInDate: request.checkInDate.slice(0, 10),
          checkOutDate: request.checkOutDate.slice(0, 10),
          status: request.status,
          serviceCount: request.serviceCount,
          assignedRoomNumber: allocationRoomNumber(request.allocations),
          assignedPropertyId: request.allocations[0]?.propertyId != null
            ? data.properties.find((property) =>
                property.propertyId === Number(request.allocations[0].propertyId),
              )?.publicId ?? String(request.allocations[0].propertyId)
            : request.propertyPublicId,
          assignedPropertyRoomTypeId: request.allocations[0]?.propertyRoomTypeId != null
            ? Number(request.allocations[0].propertyRoomTypeId)
            : request.propertyRoomTypeId,
        })),
      ),
  );

  const properties: Property[] = data.properties.map((property) => ({
    id: property.publicId,
    name: property.propertyName,
    location: property.venueName || property.city,
    tourType: "hotel",
    totalRooms: property.totalRooms,
    totalPax: property.occupiedRooms,
    icon: "🏨",
    rooms: property.rooms.flatMap((roomType) =>
      roomType.roomNames.map((roomName, index) => ({
        id: `${property.publicId}:${roomType.publicId}:${roomName}`,
        number: `#${roomName}`,
        floor: Number(roomName.charAt(0)) || 0,
        type: roomType.roomTypeName,
        propertyRoomTypeId: roomType.propertyRoomTypeId,
        roomTypePublicId: roomType.publicId,
        capacity:
          roomType.maxOccupancy || roomType.maxAdults + roomType.maxChildren,
        occupied: 0,
        status: roomType.isAvailable ? "available" : "blocked",
        occupants: [],
        nightlyRate: 0,
        housekeepingDone: false,
        laundryDone: false,
        familyId: `${roomType.publicId}-${index}`,
      })),
    ),
  }));

  return { tours, roomRequests, properties };
};

const TOUR_OPERATOR_BY_APPLICANT: Record<string, string> = {
  A001: "Saifee Tours & Travel",
  A002: "Saifee Tours & Travel",
  A003: "Saifee Tours & Travel",
  A004: "Miqaat Travel Services",
  A005: "Miqaat Travel Services",
  A006: "Al Burhani Tours",
  A007: "Al Burhani Tours",
  A008: "Al Burhani Tours",
  A009: "Al Burhani Tours",
  A010: "Faiz-e-Husaini Dubai",
  NC001: "Al Burhani Tours",
  NC002: "Miqaat Travel Services",
  NC003: "Al Burhani Tours",
  "TEST-301": "Najaf Instant Tours",
  "TEST-302": "Najaf Instant Tours",
  "TEST-401": "External Hotel Operator",
  "TEST-402": "External Hotel Operator",
  A011: "Burhani Family Tours",
  A012: "Burhani Family Tours",
  A013: "Burhani Family Tours",
  A014: "Burhani Family Tours",
  "TEST-501": "Burhani Family Tours",
  "TEST-502": "Burhani Family Tours",
};

const TOUR_NAME_BY_APPLICANT: Record<string, string> = {
  A001: "Miqaat Kerbala 2026",
  A002: "Miqaat Kerbala 2026",
  A003: "Miqaat Kerbala 2026",
  A004: "Miqaat Kerbala 2026",
  A005: "Miqaat Kerbala 2026",
  A006: "Ziyarat Najaf 2026",
  A007: "Ziyarat Najaf 2026",
  A008: "Ziyarat Najaf 2026",
  A009: "Ziyarat Najaf 2026",
  A010: "Ziyarat Najaf 2026",
  NC001: "Ziyarat Najaf 2026",
  NC002: "Miqaat Kerbala 2026",
  NC003: "Ziyarat Najaf 2026",
  A011: "Family Shared Rooms Test Tour",
  A012: "Family Shared Rooms Test Tour",
  A013: "Family Shared Rooms Test Tour",
  A014: "Family Shared Rooms Test Tour",
  "TEST-501": "Family Shared Rooms Test Tour",
  "TEST-502": "Family Shared Rooms Test Tour",
  "TEST-301": "Instant Najaf Test Tour",
  "TEST-302": "Instant Najaf Test Tour",
  "TEST-401": "External Hotel Test Tour",
  "TEST-402": "External Hotel Test Tour",
};

/* ─── Service badges ──────────────────────────────────────────────────── */
const ServiceBadges = ({ requests }: { requests: ServiceRequest[] }) => (
  <>
    {requests
      .filter((sr) => sr.type !== "mehram")
      .map((sr, i) => {
        const cfg = SERVICE_CFG[sr.type] ?? {
          icon: null,
          color: "bg-gray-100 text-gray-600 border-gray-300",
        };
        return (
          <span
            key={i}
            className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full border ${cfg.color}`}
          >
            {cfg.icon} {sr.label}
          </span>
        );
      })}
  </>
);

const ApplicantHoverCard = ({
  applicant,
  align = "left",
  children,
}: {
  applicant: Applicant;
  align?: "left" | "right";
  children: React.ReactNode;
}) => (
  <div className="group/applicant relative inline-flex" tabIndex={0}>
    {children}
    <div
      className={`pointer-events-none invisible absolute top-full z-50 mt-2 w-64 translate-y-1 rounded-xl border border-slate-200 bg-white p-3 opacity-0 shadow-xl transition-all group-hover/applicant:visible group-hover/applicant:translate-y-0 group-hover/applicant:opacity-100 group-focus-within/applicant:visible group-focus-within/applicant:translate-y-0 group-focus-within/applicant:opacity-100 ${align === "right" ? "right-0" : "left-0"}`}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-base ${applicant.gender === "Female" ? "bg-rose-100" : "bg-sky-100"}`}
        >
          {applicant.gender === "Female" ? "👩" : "👨"}
        </div>
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold text-slate-900">
            {applicant.name}
          </div>
          <div className="mt-0.5 text-[10px] text-slate-500">
            ITS {applicant.itsId}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-600">
            <span>{applicant.gender}</span>
            <span>{applicant.age} years</span>
            {applicant.isHof && (
              <span className="font-semibold text-sky-700">Head of family</span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-sky-50 px-2.5 py-2">
        <div className="min-w-0">
          <div className="truncate text-[10px] font-semibold text-sky-900">
            {applicant.tourName ||
              TOUR_NAME_BY_APPLICANT[applicant.id] ||
              "Tour not available"}
          </div>
          <div className="mt-0.5 truncate text-[9px] text-sky-600">
            {applicant.tourOperator ||
              TOUR_OPERATOR_BY_APPLICANT[applicant.id] ||
              "Tour Operator not available"}
          </div>
        </div>
        {applicant.roomAssigned && (
          <Badge className="border-emerald-200 bg-white text-[9px] text-emerald-700">
            Room {applicant.roomAssigned}
          </Badge>
        )}
      </div>
      {applicant.serviceRequests.some(
        (request) => request.type !== "mehram",
      ) && (
        <div className="mt-2 flex flex-wrap gap-1">
          <ServiceBadges requests={applicant.serviceRequests} />
        </div>
      )}
    </div>
  </div>
);

/* ─── Room Card (grid view) ───────────────────────────────────────────── */
const RoomCard = ({
  room,
  propertyId,
  onDropAssignment,
  onReallocateRoom,
  allocated,
  selected,
  onSelect,
  tooltipAlign = "center",
}: {
  room: Room;
  propertyId: string;
  onDropAssignment: (
    assignment: DragAssignment,
    propertyId: string,
    roomId: string,
  ) => void;
  onReallocateRoom: (propertyId: string, room: Room) => void;
  allocated: boolean;
  selected: boolean;
  onSelect: () => void;
  tooltipAlign?: "left" | "center" | "right";
}) => {
  const [isDragTarget, setIsDragTarget] = useState(false);
  const rtc = roomTypeTone(room.type);
  const isM = room.status === "maintenance";
  const isB = room.status === "blocked";
  const statusColor = allocated
    ? "bg-emerald-500"
    : isB
      ? "bg-red-500"
      : isM
        ? "bg-orange-500"
        : room.occupied > 0
          ? "bg-emerald-500"
          : "bg-amber-500";
  return (
    <Card
      onClick={() => {
        if (!isM && !isB) onSelect();
      }}
      onDragOver={(event) => {
        if (!allocated && !isM && !isB) {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setIsDragTarget(true);
        }
      }}
      onDragLeave={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
          setIsDragTarget(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragTarget(false);
        if (allocated || isM || isB) return;
        try {
          const assignment = JSON.parse(
            event.dataTransfer.getData("application/x-fhims-allocation"),
          ) as DragAssignment;
          onDropAssignment(assignment, propertyId, room.id);
        } catch {
          toast.error("Unable to read the applicant allocation");
        }
      }}
      className={`group/room relative h-[62px] min-h-[62px] gap-0 overflow-visible rounded-lg border-2 py-0 transition-all hover:shadow-md ${isDragTarget ? "scale-105 border-sky-600 bg-sky-100 ring-4 ring-sky-300 shadow-xl" : selected ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200" : allocated ? "border-emerald-500 bg-emerald-50/40 hover:border-emerald-600" : isB ? "border-red-400 bg-red-50/30" : isM ? "border-orange-400 bg-orange-50/30" : room.occupied > 0 ? "border-emerald-500 bg-emerald-50/40 hover:border-emerald-600" : "border-amber-400 bg-amber-50/35 hover:border-amber-500"}`}
    >
      {isDragTarget && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md bg-sky-600 text-[10px] font-bold text-white shadow-inner">
          Drop in room&nbsp;<span className="text-sm">{room.number}</span>
        </div>
      )}
      <div
        className={`absolute inset-x-1.5 top-0 h-0.5 rounded-b-full ${statusColor}`}
      />
      <CardContent className="flex h-full min-w-0 flex-col p-1.5 pt-2">
        <div className="flex min-w-0 items-center gap-1">
          <div
            className={`min-w-0 flex-1 whitespace-nowrap text-[8px] font-semibold ${rtc.text}`}
          >
            {room.type}
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {isM && !allocated && (
              <span
                title="Maintenance"
                className="flex h-4 w-4 items-center justify-center rounded bg-orange-100 text-orange-600"
              >
                <Wrench className="h-2.5 w-2.5" />
              </span>
            )}
            {isB && !allocated && (
              <span
                title="Blocked"
                className="flex h-4 w-4 items-center justify-center rounded bg-red-100 text-red-600"
              >
                <ShieldOff className="h-2.5 w-2.5" />
              </span>
            )}
          </div>
        </div>
        <div className="mt-0.5 flex items-end justify-between gap-1">
          <div className="text-[14px] font-bold leading-none text-slate-900">
            {room.number}
          </div>
        </div>
        {allocated && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onReallocateRoom(propertyId, room);
            }}
            className="hover:text-purple-800 hover:underline mt-auto flex w-full items-center justify-center gap-1 border-t border-emerald-200 pt-1 text-[9px] font-semibold leading-none text-violet-700 hover:text-violet-900"
          >
            <Repeat2 className="h-2.5 w-2.5" /> Reallocate
          </button>
        )}
      </CardContent>
      {!!room.occupants?.length && (
        <div
          className={`pointer-events-none invisible absolute bottom-full z-50 mb-2 w-52 translate-y-1 rounded-lg border border-slate-200 bg-white p-2.5 opacity-0 shadow-xl transition-all group-hover/room:visible group-hover/room:translate-y-0 group-hover/room:opacity-100 ${tooltipAlign === "left" ? "left-0" : tooltipAlign === "right" ? "right-0" : "left-1/2 -translate-x-1/2"}`}
        >
          <div className="space-y-1.5">
            {room.occupants.map((passenger) => (
              <div
                key={passenger.id}
                className="flex items-center gap-2 text-[10px] font-medium text-slate-700"
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full border ${passengerTone(passenger.age)}`}
                />
                <span className="truncate">{passenger.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

const FamilySummaryCard = ({
  family,
  index,
  tourId,
  onReallocate,
  onDeallocate,
}: {
  family: Family;
  index: number;
  tourId: string;
  onReallocate: (tourId: string, applicant: Applicant) => void;
  onDeallocate: (tourId: string, applicant: Applicant) => void;
}) => {
  const p = PALETTES[family.colorKey] ?? pal(index);
  const assignedCount = family.members.filter(
    (member) => member.roomAssigned,
  ).length;
  const roomNumbers = [
    ...new Set(
      family.members.map((member) => member.roomAssigned).filter(Boolean),
    ),
  ];

  return (
    <div className={`rounded-xl border ${p.border} bg-white p-2.5 shadow-sm`}>
      <div
        className="flex cursor-grab items-center gap-2.5 rounded-lg active:cursor-grabbing"
        draggable
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData(
            "application/x-fhims-allocation",
            JSON.stringify({
              kind: "family",
              tourId,
              familyId: family.id,
            } satisfies DragAssignment),
          );
        }}
      >
        <div
          className={`${p.face} flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-[10px] font-bold text-white`}
        >
          {family.familyCode}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold text-slate-900">
            {family.hofName}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[9px] text-slate-500">
            <span>HOF {family.hofItsId}</span>
            <span>•</span>
            <span>{family.members.length} applicants</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-medium text-slate-500">
            {assignedCount}/{family.members.length} placed
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between rounded-lg border border-dashed border-violet-200 bg-violet-50/60 px-2 py-1.5 text-[9px] font-medium text-violet-700">
        <span className="flex items-center gap-1">
          <Repeat2 className="h-3 w-3" /> Drag this row to move the complete
          family
        </span>
        <div className="flex flex-wrap justify-end gap-1">
          {roomNumbers.map((room) => (
            <Badge
              key={room}
              className="border-slate-200 bg-white px-1.5 py-0 text-[9px] text-slate-600"
            >
              Room {room}
            </Badge>
          ))}
        </div>
      </div>

      <div className="mt-2 space-y-1.5">
        {family.members.map((member) => (
          <div
            key={member.id}
            className="cursor-grab rounded-lg border border-slate-100 bg-slate-50/70 p-2 transition-colors hover:border-sky-200 hover:bg-sky-50/50 active:cursor-grabbing"
            draggable
            onDragStart={(event) => {
              event.stopPropagation();
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData(
                "application/x-fhims-allocation",
                JSON.stringify({
                  kind: "applicant",
                  tourId,
                  applicantId: member.id,
                } satisfies DragAssignment),
              );
            }}
          >
            <div className="flex items-start gap-2">
              <ApplicantHoverCard applicant={member} align="right">
                <div
                  className={`flex h-7 w-7 flex-shrink-0 cursor-help items-center justify-center rounded-full ${member.gender === "Female" ? "bg-rose-100" : "bg-sky-100"}`}
                >
                  {member.gender === "Female" ? "👩" : "👨"}
                </div>
              </ApplicantHoverCard>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[10px] font-semibold text-slate-900">
                  {member.name || "jugnu"}
                </div>
                <div className="text-[9px] text-slate-500">
                  ITS {member.itsId} · {member.gender} · {member.age} years{" "}
                  {member.isHof ? "· HOF" : ""}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                <Badge
                  className={`${member.roomAssigned ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-600"} px-1.5 py-0 text-[9px]`}
                >
                  {member.roomAssigned
                    ? `Room ${member.roomAssigned}`
                    : "Unassigned"}
                </Badge>
                {member.roomAssigned && (
                  <>
                    <button
                      title="Reallocate applicant"
                      onClick={(event) => {
                        event.stopPropagation();
                        onReallocate(tourId, member);
                      }}
                      className="rounded border border-violet-200 p-1 text-violet-600 hover:bg-violet-50"
                    >
                      <Repeat2 className="h-3 w-3" />
                    </button>
                    <button
                      title="Deallocate applicant"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDeallocate(tourId, member);
                      }}
                      className="rounded border border-red-200 p-1 text-red-500 hover:bg-red-50"
                    >
                      <UserMinus className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ApplicantSummaryCard = ({
  applicant,
  tourId,
  onReallocate,
  onDeallocate,
}: {
  applicant: Applicant;
  tourId: string;
  onReallocate: (tourId: string, applicant: Applicant) => void;
  onDeallocate: (tourId: string, applicant: Applicant) => void;
}) => (
  <div
    className="flex cursor-grab items-center gap-2 rounded-xl border border-amber-200 bg-white p-2.5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing"
    draggable
    onDragStart={(event) => {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(
        "application/x-fhims-allocation",
        JSON.stringify({
          kind: "applicant",
          tourId,
          applicantId: applicant.id,
        } satisfies DragAssignment),
      );
    }}
  >
    <ApplicantHoverCard applicant={applicant} align="right">
      <div
        className={`flex h-9 w-9 flex-shrink-0 cursor-help items-center justify-center rounded-full text-base ${applicant.gender === "Female" ? "bg-rose-100" : "bg-sky-100"}`}
      >
        {applicant.gender === "Female" ? "👩" : "👨"}
      </div>
    </ApplicantHoverCard>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[11px] font-semibold text-slate-900">
        {applicant.name}
      </div>
      <div className="mt-0.5 text-[9px] text-slate-500">
        ITS {applicant.itsId} · {applicant.gender} · {applicant.age} years
      </div>
    </div>
    <div className="flex flex-shrink-0 items-center gap-1">
      <Badge
        className={`${applicant.roomAssigned ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-600"} px-1.5 py-0 text-[9px]`}
      >
        {applicant.roomAssigned
          ? `Room ${applicant.roomAssigned}`
          : "Unassigned"}
      </Badge>
      {applicant.roomAssigned && (
        <>
          <button
            title="Reallocate applicant"
            onClick={() => onReallocate(tourId, applicant)}
            className="rounded border border-violet-200 p-1 text-violet-600 hover:bg-violet-50"
          >
            <Repeat2 className="h-3 w-3" />
          </button>
          <button
            title="Deallocate applicant"
            onClick={() => onDeallocate(tourId, applicant)}
            className="rounded border border-red-200 p-1 text-red-500 hover:bg-red-50"
          >
            <UserMinus className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  </div>
);

/* ─── Main Component ──────────────────────────────────────────────────── */
export const AccommodationDashboardEnhanced = () => {
  const { setPageHeader } = usePageHeader();
  const [dateFrom, setDateFrom] = useState(TODAY);
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [venueFilter, setVenueFilter] = useState("all");
  const [venues, setVenues] = useState<VenueApi[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [showNonAvailableRooms, setShowNonAvailableRooms] = useState(false);
  const [showNonRelatedRooms, setShowNonRelatedRooms] = useState(false);
  const [collapsedTours, setCollapsedTours] = useState<Set<string>>(new Set());

  /* family assignment state */
  const [tours, setTours] = useState<Tour[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [roomRequests, setRoomRequests] = useState<TourRoomRequest[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardRefreshVersion, setDashboardRefreshVersion] = useState(0);
  const [todayMovement, setTodayMovement] = useState({
    checkIns: 0,
    checkOuts: 0,
    occupancyPercentage: 0,
  });
  const [reallocationTarget, setReallocationTarget] = useState<{
    tourId: string;
    applicant: Applicant;
  } | null>(null);
  const [deallocationTarget, setDeallocationTarget] = useState<{
    tourId: string;
    applicant: Applicant;
  } | null>(null);
  const [deallocationReason, setDeallocationReason] = useState("");
  const [targetPropertyId, setTargetPropertyId] = useState("");
  const [targetRoomId, setTargetRoomId] = useState("");
  const [reallocationReason, setReallocationReason] = useState("");
  const [reallocationCharge, setReallocationCharge] = useState(0);
  const [reallocationDiscountType, setReallocationDiscountType] =
    useState("none");
  const [reallocationDiscount, setReallocationDiscount] = useState(0);
  const [applicantDialogOpen, setApplicantDialogOpen] = useState(false);
  const [approvedApplicantSearch, setApprovedApplicantSearch] = useState("");
  const [remainingApprovedApplicants, setRemainingApprovedApplicants] =
    useState<ApprovedApplicant[]>([]);
  const [selectedApprovedApplicantIds, setSelectedApprovedApplicantIds] =
    useState<Set<string>>(new Set());
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [tourApplicantSearch, setTourApplicantSearch] = useState("");
  const [selectedRoomRequestId, setSelectedRoomRequestId] = useState("");
  const [selectedRequestRoomType, setSelectedRequestRoomType] = useState("");
  const [selectedRequestPropertyId, setSelectedRequestPropertyId] =
    useState("");
  const [
    selectedRequestPropertyRoomTypeId,
    setSelectedRequestPropertyRoomTypeId,
  ] = useState<number | undefined>();
  const [selectedRequestRoomIndex, setSelectedRequestRoomIndex] = useState(0);
  const [selectedRequestApplicantIds, setSelectedRequestApplicantIds] =
    useState<Set<string>>(new Set());
  const [selectedAllocationRoomId, setSelectedAllocationRoomId] = useState("");
  const [roomAssignmentSaving, setRoomAssignmentSaving] = useState(false);
  const [autoAllocationTarget, setAutoAllocationTarget] = useState<{
    tourPublicId: string;
    tourName: string;
    tourCode: string;
  } | null>(null);
  const [requestRoomAssignments, setRequestRoomAssignments] = useState<
    Record<string, string>
  >({});
  const [draggingRequestSlotKey, setDraggingRequestSlotKey] = useState("");
  const [locallyUnlinkedRequestSlots, setLocallyUnlinkedRequestSlots] =
    useState<Set<string>>(new Set());
  const [roomUnallocationTarget, setRoomUnallocationTarget] = useState<{
    request: TourRoomRequest;
    roomIndex: number;
    assignedRoomNumber?: string;
  } | null>(null);
  const [roomUnallocationSaving, setRoomUnallocationSaving] = useState(false);
  const [tourDropdownOpen, setTourDropdownOpen] = useState(false);
  const [tourDropdownSearch, setTourDropdownSearch] = useState("");
  const tourDropdownRef = useRef<HTMLDivElement>(null);
  const [roomReallocationTarget, setRoomReallocationTarget] = useState<{
    propertyId: string;
    room: Room;
    request: TourRoomRequest;
    roomIndex: number;
  } | null>(null);
  // UI selection only until the available-properties API supports property type.
  const [roomTargetPropertyTypeId, setRoomTargetPropertyTypeId] = useState("");
  const [roomPropertyTypes, setRoomPropertyTypes] = useState<PropertyTypeApi[]>([]);
  const [roomPropertyTypesLoading, setRoomPropertyTypesLoading] = useState(true);
  const [roomPropertyTypesError, setRoomPropertyTypesError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPropertyTypesReallocate()
      .then((types) => {
        if (!cancelled) setRoomPropertyTypes(types.filter((type) => type.isActive));
      })
      .catch(() => {
        if (!cancelled) {
          setRoomPropertyTypesError(true);
          toast.error("Failed to load property types");
        }
      })
      .finally(() => {
        if (!cancelled) setRoomPropertyTypesLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const [roomTargetPropertyId, setRoomTargetPropertyId] = useState("");
  const [roomTargetType, setRoomTargetType] = useState<RoomType | "">("");
  const [roomTargetRoomId, setRoomTargetRoomId] = useState("");
  const [roomTargetProperties, setRoomTargetProperties] = useState<
    AvailableAccommodationPropertyApi[]
  >([]);
  const [roomTargetPropertiesLoading, setRoomTargetPropertiesLoading] =
    useState(false);
  const [roomTargetRoomTypes, setRoomTargetRoomTypes] = useState<
    AccommodationRoomTypeOptionApi[]
  >([]);
  const [roomTargetRoomTypesLoading, setRoomTargetRoomTypesLoading] =
    useState(false);
  const [roomTargetRooms, setRoomTargetRooms] = useState<
    AvailableAccommodationRoomApi[]
  >([]);
  const [roomTargetRoomsLoading, setRoomTargetRoomsLoading] = useState(false);
  const [roomReallocationReason, setRoomReallocationReason] = useState("");
  const [roomReallocationPeriod, setRoomReallocationPeriod] = useState<
    "all" | "partial"
  >("all");
  const [roomReallocationFrom, setRoomReallocationFrom] = useState("");
  const [roomReallocationTo, setRoomReallocationTo] = useState("");
  const [reallocationPeriod, setReallocationPeriod] = useState<
    "all" | "partial"
  >("all");
  const [reallocationFrom, setReallocationFrom] = useState("");
  const [reallocationTo, setReallocationTo] = useState("");

  useEffect(() => {
    const request = roomReallocationTarget?.request;
    const startDate =
      roomReallocationPeriod === "partial"
        ? roomReallocationFrom
        : request?.checkInDate;
    const endDate =
      roomReallocationPeriod === "partial"
        ? roomReallocationTo
        : request?.checkOutDate;

    setRoomTargetPropertyId("");
    setRoomTargetType("");
    setRoomTargetRoomId("");
    setRoomTargetRoomTypes([]);
    setRoomTargetRooms([]);
    setRoomTargetRoomsLoading(false);

    if (
      !roomTargetPropertyTypeId ||
      !request?.venuePublicId ||
      !startDate ||
      !endDate ||
      startDate > endDate
    ) {
      setRoomTargetProperties([]);
      setRoomTargetPropertiesLoading(false);
      return;
    }

    let cancelled = false;
    setRoomTargetProperties([]);
    setRoomTargetPropertiesLoading(true);

    const loadAvailableProperties = async () => {
      try {
        const response = await getAvailableAccommodationProperties({
          venuePublicId: request.venuePublicId,
          propertyTypeId: roomTargetPropertyTypeId,
          startDate,
          endDate,
        });
        if (cancelled) return;
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load available properties",
          );
        }
        // console.log("Available properties:", response.data);
        setRoomTargetProperties(response.data ?? []);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load available properties:", error);
          toast.error(
            getApiErrorMessage(error, "Failed to load available properties"),
          );
        }
      } finally {
        if (!cancelled) setRoomTargetPropertiesLoading(false);
      }
    };

    void loadAvailableProperties();

    return () => {
      cancelled = true;
    };
  }, [
    roomReallocationFrom,
    roomReallocationPeriod,
    roomReallocationTarget,
    roomReallocationTo,
    roomTargetPropertyTypeId,
  ]);

  useEffect(() => {
    setRoomTargetType("");
    setRoomTargetRoomId("");
    setRoomTargetRoomTypes([]);
    setRoomTargetRooms([]);
    setRoomTargetRoomsLoading(false);

    if (!roomReallocationTarget || !roomTargetPropertyId) {
      setRoomTargetRoomTypesLoading(false);
      return;
    }

    let cancelled = false;
    setRoomTargetRoomTypesLoading(true);

    const loadRoomTypes = async () => {
      try {
        const roomTypes =
          await getAccommodationRoomTypesByProperty(roomTargetPropertyId);
        if (!cancelled) {
          setRoomTargetRoomTypes(
            roomTypes.filter((roomType) => roomType.isActive),
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load room types:", error);
          toast.error(getApiErrorMessage(error, "Failed to load room types"));
        }
      } finally {
        if (!cancelled) setRoomTargetRoomTypesLoading(false);
      }
    };

    void loadRoomTypes();

    return () => {
      cancelled = true;
    };
  }, [roomReallocationTarget, roomTargetPropertyId]);

  useEffect(() => {
    const request = roomReallocationTarget?.request;
    const startDate =
      roomReallocationPeriod === "partial"
        ? roomReallocationFrom
        : request?.checkInDate;
    const endDate =
      roomReallocationPeriod === "partial"
        ? roomReallocationTo
        : request?.checkOutDate;

    setRoomTargetRoomId("");
    setRoomTargetRooms([]);

    if (
      !request?.venuePublicId ||
      !roomTargetPropertyId ||
      !roomTargetType ||
      !startDate ||
      !endDate ||
      startDate > endDate
    ) {
      setRoomTargetRoomsLoading(false);
      return;
    }

    let cancelled = false;
    setRoomTargetRoomsLoading(true);

    const loadAvailableRooms = async () => {
      try {
        const response = await getAvailableAccommodationRooms({
          propertyPublicId: roomTargetPropertyId,
          venuePublicId: request.venuePublicId,
          startDate,
          endDate,
          roomTypePublicId: roomTargetType,
        });
        if (cancelled) return;
        if (!response.success) {
          throw new Error(response.message || "Failed to load available rooms");
        }
        setRoomTargetRooms(
          Array.from(
            new Map(
              (response.data ?? [])
                .filter((room) => !room.isBooked)
                .map((room) => [availableRoomOptionValue(room), room]),
            ).values(),
          ),
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load available rooms:", error);
          toast.error(
            getApiErrorMessage(error, "Failed to load available rooms"),
          );
        }
      } finally {
        if (!cancelled) setRoomTargetRoomsLoading(false);
      }
    };

    void loadAvailableRooms();

    return () => {
      cancelled = true;
    };
  }, [
    roomReallocationFrom,
    roomReallocationPeriod,
    roomReallocationTarget,
    roomReallocationTo,
    roomTargetPropertyId,
    roomTargetType,
  ]);

  useEffect(() => {
    if (!tourDropdownOpen) return;

    const handleOutsideClick = (event: PointerEvent) => {
      if (
        tourDropdownRef.current &&
        !tourDropdownRef.current.contains(event.target as Node)
      ) {
        setTourDropdownOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", handleOutsideClick);
  }, [tourDropdownOpen]);

  useEffect(() => {
    setPageHeader({
      title: "Room Allocation Dashboard",
      icon: <BedDouble className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  // VENUE API CALL
  useEffect(() => {
    let isMounted = true;

    const loadVenues = async () => {
      try {
        setVenuesLoading(true);
        const venueList = await getVenues();
        if (isMounted) setVenues(venueList);
      } catch (error) {
        if (isMounted) toast.error("Failed to load venues");
      } finally {
        if (isMounted) setVenuesLoading(false);
      }
    };

    loadVenues();

    return () => {
      isMounted = false;
    };
  }, []);

  //PREFILLED GET DETAILS API CALL
  useEffect(() => {
    let isMounted = true;
    const timer = window.setTimeout(async () => {
      try {
        setDashboardLoading(true);
        const selectedVenue = venues.find(
          (venue) => venue.venueName.toLowerCase() === venueFilter,
        );
        const data = await getAccommodationDashboard({
          fromDate: dateFrom || undefined,
          toDate: dateTo || undefined,
          venuePublicId:
            venueFilter === "all" ? undefined : selectedVenue?.publicId,
          search: search.trim() || undefined,
        });
        if (!isMounted) return;
        const mapped = mapAccommodationDashboard(data);
        setTours(mapped.tours);
        setRoomRequests(mapped.roomRequests);
        setProperties(mapped.properties);
        setTodayMovement(data.todayMovement);
        setRequestRoomAssignments({});
        setLocallyUnlinkedRequestSlots(new Set());
      } catch (error) {
        if (isMounted) toast.error("Failed to load accommodation dashboard");
      } finally {
        if (isMounted) setDashboardLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [dateFrom, dateTo, search, venueFilter, venues, dashboardRefreshVersion]);

  const toggleTourCollapse = (tourId: string) => {
    setCollapsedTours((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tourId)) {
        newSet.delete(tourId);
      } else {
        newSet.add(tourId);
      }
      return newSet;
    });
  };

  const filteredApprovedApplicants = remainingApprovedApplicants.filter(
    (applicant) => {
      const query = approvedApplicantSearch.trim().toLowerCase();
      return (
        !query ||
        [applicant.name, applicant.itsId, applicant.tourOperator].some(
          (value) => value.toLowerCase().includes(query),
        )
      );
    },
  );

  const toggleApprovedApplicant = (applicantId: string) => {
    setSelectedApprovedApplicantIds((current) => {
      const next = new Set(current);
      if (next.has(applicantId)) next.delete(applicantId);
      else next.add(applicantId);
      return next;
    });
  };

  const addApprovedApplicants = () => {
    if (!selectedApprovedApplicantIds.size) {
      toast.error("Select at least one applicant");
      return;
    }
    const selected = remainingApprovedApplicants.filter((applicant) =>
      selectedApprovedApplicantIds.has(applicant.id),
    );
    const manuallyAdded = selected.map(
      ({
        tourId: _tourId,
        approvedOn: _approvedOn,
        tourName: _tourName,
        ...applicant
      }) => ({ ...applicant, tourName: "Unassigned" }),
    );
    setTours((current) => {
      const existing = current.find(
        (tour) => tour.id === MANUAL_UNASSIGNED_TOUR_ID,
      );
      if (existing) {
        return current.map((tour) =>
          tour.id === MANUAL_UNASSIGNED_TOUR_ID
            ? {
                ...tour,
                nonClubbedApplicants: [
                  ...tour.nonClubbedApplicants,
                  ...manuallyAdded,
                ],
              }
            : tour,
        );
      }
      return [
        {
          id: MANUAL_UNASSIGNED_TOUR_ID,
          name: "Unassigned",
          tourType: "instant",
          startDate: TODAY,
          endDate: TODAY,
          propertyId: "",
          clubbedFamilies: [],
          nonClubbedApplicants: manuallyAdded,
        },
        ...current,
      ];
    });
    setRemainingApprovedApplicants((current) =>
      current.filter(
        (applicant) => !selectedApprovedApplicantIds.has(applicant.id),
      ),
    );
    setSelectedApprovedApplicantIds(new Set());
    setApprovedApplicantSearch("");
    setApplicantDialogOpen(false);
    toast.success(
      `${selected.length} applicant${selected.length === 1 ? "" : "s"} added for room allocation`,
    );
  };

  const openReallocation = (tourId: string, applicant: Applicant) => {
    const currentProperty = properties.find((property) =>
      property.rooms.some((room) => room.id === applicant.roomAssigned),
    );
    setReallocationTarget({ tourId, applicant });
    setTargetPropertyId(currentProperty?.id || properties[0]?.id || "");
    setTargetRoomId("");
    setReallocationReason("");
    setReallocationCharge(0);
    setReallocationDiscountType("none");
    setReallocationDiscount(0);
    setReallocationPeriod("all");
    setReallocationFrom("");
    setReallocationTo("");
  };

  const saveReallocation = () => {
    if (
      !reallocationTarget ||
      !targetPropertyId ||
      !targetRoomId ||
      !reallocationReason.trim() ||
      (reallocationPeriod === "partial" &&
        (!reallocationFrom ||
          !reallocationTo ||
          reallocationFrom > reallocationTo))
    ) {
      toast.error(
        "Select a property and room, enter a reason, and provide a valid date range",
      );
      return;
    }
    const targetRoom = properties
      .flatMap((property) => property.rooms)
      .find((room) => room.id === targetRoomId);
    if (
      !targetRoom ||
      targetRoom.occupied >= targetRoom.capacity ||
      ["maintenance", "blocked"].includes(targetRoom.status)
    ) {
      toast.error("Selected room is not available");
      return;
    }
    const applicant = reallocationTarget.applicant;
    setTours((current) =>
      current.map((tour) =>
        tour.id !== reallocationTarget.tourId
          ? tour
          : {
              ...tour,
              propertyId: targetPropertyId,
              clubbedFamilies: tour.clubbedFamilies.map((family) => ({
                ...family,
                members: family.members.map((member) =>
                  member.id === applicant.id
                    ? { ...member, roomAssigned: targetRoomId }
                    : member,
                ),
              })),
              nonClubbedApplicants: tour.nonClubbedApplicants.map((member) =>
                member.id === applicant.id
                  ? { ...member, roomAssigned: targetRoomId }
                  : member,
              ),
            },
      ),
    );
    setProperties((current) =>
      current.map((property) => ({
        ...property,
        rooms: property.rooms.map((room) => {
          const occupants = (room.occupants || []).filter(
            (member) => member.id !== applicant.id,
          );
          if (property.id === targetPropertyId && room.id === targetRoomId)
            occupants.push({ ...applicant, roomAssigned: targetRoomId });
          const occupied = occupants.length;
          const status: RoomStatus =
            room.status === "maintenance" || room.status === "blocked"
              ? room.status
              : occupied === 0
                ? "available"
                : occupied >= room.capacity
                  ? "occupied"
                  : "partial";
          return {
            ...room,
            occupants,
            occupied,
            status,
            additionalCharges:
              property.id === targetPropertyId && room.id === targetRoomId
                ? reallocationCharge
                : room.additionalCharges,
            discount:
              property.id === targetPropertyId && room.id === targetRoomId
                ? reallocationDiscount
                : room.discount,
          };
        }),
      })),
    );
    toast.success(
      "Booking modification completed. " +
        (reallocationCharge > 0
          ? "Additional charge applied."
          : "No additional charge applied.") +
        (reallocationDiscountType !== "none" ? " Discount recorded." : ""),
    );
    setReallocationTarget(null);
  };

  const selectRoomReallocationDestination = (value: string) => {
    setRoomTargetRoomId(value);

    const destinationRoom = roomTargetRooms.find(
      (room) => availableRoomOptionValue(room) === value,
    );
    const currentRoomNumber = roomReallocationTarget?.room.number;
    const currentRoomType = roomReallocationTarget?.room.type;
    const destinationRoomType = roomTargetRoomTypes.find(
      (roomType) => roomType.publicId === roomTargetType,
    )?.roomName;
    if (!destinationRoom || !currentRoomNumber) {
      setRoomReallocationReason("");
      return;
    }

    const previousRoom = `#${String(currentRoomNumber).replace(/^#+/, "")}`;
    const newRoom = `#${String(destinationRoom.roomNumber).replace(/^#+/, "")}`;

    if (
      currentRoomType &&
      destinationRoomType &&
      normalizedRoomType(currentRoomType) !==
        normalizedRoomType(destinationRoomType)
    ) {
      setRoomReallocationReason(
        `Room type changed from ${currentRoomType} to ${destinationRoomType}, and room number changed from ${previousRoom} to ${newRoom}.`,
      );
      return;
    }

    setRoomReallocationReason(
      `Room number changed from ${previousRoom} to ${newRoom}.`,
    );
  };

  // ROOM REALLOCATION SAVE FUNCTION
  const saveRoomReallocation = async () => {
    if (
      !roomReallocationTarget ||
      !roomTargetPropertyId ||
      !roomTargetType ||
      !roomTargetRoomId ||
      !roomReallocationReason.trim() ||
      (roomReallocationPeriod === "partial" &&
        (!roomReallocationFrom ||
          !roomReallocationTo ||
          roomReallocationFrom > roomReallocationTo))
    ) {
      toast.error(
        "Select the destination, enter a reason, and provide a valid date range",
      );
      return;
    }
    if (roomAssignmentSaving) return;

    const targetProperty = roomTargetProperties.find(
      (property) => property.propertyPublicId === roomTargetPropertyId,
    );
    const targetRoomType = roomTargetRoomTypes.find(
      (roomType) => roomType.publicId === roomTargetType,
    );
    const targetRoom = roomTargetRooms.find(
      (room) => availableRoomOptionValue(room) === roomTargetRoomId,
    );
    if (!targetProperty || !targetRoomType || !targetRoom) {
      toast.error("The selected destination room is unavailable");
      return;
    }
    if (targetRoom.isBooked) {
      toast.error("The selected destination room is already booked");
      return;
    }
    if (
      targetProperty.propertyPublicId === roomReallocationTarget.propertyId &&
      roomNumberKey(targetRoom.roomNumber) ===
        roomNumberKey(roomReallocationTarget.room.number)
    ) {
      toast.error("Select a room different from the current room");
      return;
    }

    const targetInventoryRoom = properties
      .find((property) => property.id === targetProperty.propertyPublicId)
      ?.rooms.find(
        (room) =>
          roomNumberKey(room.number) === roomNumberKey(targetRoom.roomNumber) &&
          (room.roomTypePublicId === targetRoomType.publicId ||
            normalizedRoomType(room.type) ===
              normalizedRoomType(targetRoomType.roomName)),
      );

    if (!roomReallocationTarget.request.roomAllocationPublicId) {
      toast.error("Room allocation ID is missing from the room request");
      return;
    }

    const allDays = roomReallocationPeriod === "all";
    const selectedFromDate = allDays
      ? roomReallocationTarget.request.checkInDate
      : roomReallocationFrom;
    const selectedToDate = allDays
      ? roomReallocationTarget.request.checkOutDate
      : roomReallocationTo;
    const toApiDateTime = (value: string) =>
      value.includes("T") ? value : `${value}T00:00:00.000Z`;
    const payload: ReallocateAccommodationRoomPayload = {
      roomAllocationPublicId:
        roomReallocationTarget.request.roomAllocationPublicId,
      destinationPropertyPublicId: targetProperty.propertyPublicId,
      destinationRoomTypePublicId: targetRoom.roomPublicId,
      destinationRoomNumber: targetRoom.roomNumber.replace(/^#/, ""),
      allDays,
      fromDate: toApiDateTime(selectedFromDate),
      toDate: toApiDateTime(selectedToDate),
      reason: roomReallocationReason.trim(),
    };

    setRoomAssignmentSaving(true);
    try {
      // console.log("Room reallocation payload:", payload);
      const response = await reallocateAccommodationRoom(
        roomReallocationTarget.request.tourId,
        payload,
      );
      if (!response.success) {
        toast.error(response.message || "Failed to reallocate the room");
        return;
      }

      const assignmentKey = requestSlotKey(
        roomReallocationTarget.request.id,
        roomReallocationTarget.roomIndex,
      );
      setRequestRoomAssignments((current) => {
        const next = { ...current };
        if (targetInventoryRoom) {
          next[assignmentKey] = targetInventoryRoom.id;
        } else {
          delete next[assignmentKey];
        }
        return next;
      });
      setRoomRequests((current) =>
        current.map((request) =>
          request.id === roomReallocationTarget.request.id
            ? {
                ...request,
                propertyId: targetProperty.propertyPublicId,
                roomType: targetRoomType.roomName,
                propertyRoomTypeId: targetInventoryRoom?.propertyRoomTypeId,
                assignedRoomNumber: `#${targetRoom.roomNumber.replace(/^#/, "")}`,
              }
            : request,
        ),
      );
      setSelectedAllocationRoomId("");
      setRoomReallocationTarget(null);
      toast.success(
        response.message ||
          `Room reallocated to ${targetProperty.propertyName} #${targetRoom.roomNumber.replace(/^#/, "")}`,
      );
      setDashboardRefreshVersion((current) => current + 1);
    } catch (error) {
      console.error("Failed to reallocate accommodation room:", error);
      toast.error(getApiErrorMessage(error, "Failed to reallocate the room"));
    } finally {
      setRoomAssignmentSaving(false);
    }
  };

  const openDeallocation = (tourId: string, applicant: Applicant) => {
    setDeallocationTarget({ tourId, applicant });
    setDeallocationReason("");
  };

  const saveDeallocation = () => {
    if (!deallocationTarget || !deallocationReason.trim()) {
      toast.error("Enter a reason for deallocation");
      return;
    }
    const applicant = deallocationTarget.applicant;
    setTours((current) =>
      current.map((tour) =>
        tour.id !== deallocationTarget.tourId
          ? tour
          : {
              ...tour,
              clubbedFamilies: tour.clubbedFamilies.map((family) => ({
                ...family,
                members: family.members.map((member) =>
                  member.id === applicant.id
                    ? { ...member, roomAssigned: undefined }
                    : member,
                ),
              })),
              nonClubbedApplicants: tour.nonClubbedApplicants.map((member) =>
                member.id === applicant.id
                  ? { ...member, roomAssigned: undefined }
                  : member,
              ),
            },
      ),
    );
    setProperties((current) =>
      current.map((property) => ({
        ...property,
        rooms: property.rooms.map((room) => {
          const occupants = (room.occupants || []).filter(
            (member) => member.id !== applicant.id,
          );
          const occupied = occupants.length;
          const status: RoomStatus =
            room.status === "maintenance" || room.status === "blocked"
              ? room.status
              : occupied === 0
                ? "available"
                : occupied >= room.capacity
                  ? "occupied"
                  : "partial";
          return { ...room, occupants, occupied, status };
        }),
      })),
    );
    toast.success(applicant.name + " deallocated. Reason recorded.");
    setDeallocationTarget(null);
  };

  const dropAssignmentOnRoom = async (
    assignment: DragAssignment,
    propertyId: string,
    roomId: string,
  ): Promise<boolean> => {
    const assignmentSlotKey =
      assignment.requestId !== undefined && assignment.roomIndex !== undefined
        ? requestSlotKey(assignment.requestId, assignment.roomIndex)
        : undefined;
    const previousRequestRoomId = assignmentSlotKey
      ? requestRoomAssignments[assignmentSlotKey]
      : undefined;
    const sourceTour = tours.find((tour) => tour.id === assignment.tourId);
    const targetProperty = properties.find(
      (property) => property.id === propertyId,
    );
    const targetRoom = targetProperty?.rooms.find((room) => room.id === roomId);
    if (!sourceTour || !targetProperty || !targetRoom) {
      toast.error("The selected applicant or room is no longer available");
      return false;
    }
    if (
      targetRoom.status === "maintenance" ||
      targetRoom.status === "blocked"
    ) {
      toast.error("Applicants cannot be assigned to an unavailable room");
      return false;
    }

    const family =
      assignment.kind === "family"
        ? sourceTour.clubbedFamilies.find(
            (item) => item.id === assignment.familyId,
          )
        : undefined;
    const applicant =
      assignment.kind === "applicant"
        ? [
            ...sourceTour.clubbedFamilies.flatMap((item) => item.members),
            ...sourceTour.nonClubbedApplicants,
          ].find((item) => item.id === assignment.applicantId)
        : undefined;
    const groupApplicants =
      assignment.kind === "group"
        ? [
            ...sourceTour.clubbedFamilies.flatMap((item) => item.members),
            ...sourceTour.nonClubbedApplicants,
          ].filter((item) => assignment.applicantIds?.includes(item.id))
        : [];
    const movingApplicants =
      family?.members ?? (applicant ? [applicant] : groupApplicants);
    if (!movingApplicants.length && !assignmentSlotKey) {
      toast.error("No applicants were found for this allocation");
      return false;
    }

    const movingIds = new Set(movingApplicants.map((item) => item.id));
    const existingTargetOccupants = (targetRoom.occupants ?? []).filter(
      (item) => !movingIds.has(item.id),
    );
    if (
      existingTargetOccupants.length + movingApplicants.length >
      targetRoom.capacity
    ) {
      toast.error(
        `This room has space for only ${targetRoom.capacity - existingTargetOccupants.length} more applicant(s)`,
      );
      return false;
    }
    if (
      targetRoom.type === "Shared-Gents" &&
      movingApplicants.some((item) => item.gender !== "Male")
    ) {
      toast.error("Only male applicants can be assigned to a Gents room");
      return false;
    }
    if (
      targetRoom.type === "Shared-Ladies" &&
      movingApplicants.some((item) => item.gender !== "Female")
    ) {
      toast.error("Only female applicants can be assigned to a Ladies room");
      return false;
    }
    if (
      family &&
      targetRoom.familyId &&
      targetRoom.familyId !== family.id &&
      existingTargetOccupants.length > 0
    ) {
      toast.error("This room is already marked for another family");
      return false;
    }

    let apiSuccessMessage: string | undefined;
    if (assignment.requestId !== undefined) {
      const roomRequest = roomRequests.find(
        (request) => request.id === assignment.requestId,
      );
      if (!roomRequest?.roomAllocationPublicId) {
        toast.error("Room allocation ID is missing from the room request");
        return false;
      }
      if (!targetRoom.roomTypePublicId) {
        toast.error("Room type ID is missing from the selected room");
        return false;
      }

      const payload = {
        roomAllocationPublicId: roomRequest.roomAllocationPublicId,
        roomNumber: targetRoom.number.replace(/^#/, ""),
        roomTypePublicId: targetRoom.roomTypePublicId,
      };

      try {
        const response = await allocateAccommodationRoom(payload);
        if (!response.success) {
          toast.error(
            response.message || "Failed to allocate the selected room",
          );
          return false;
        }
        apiSuccessMessage = response.message;
        setDashboardRefreshVersion((current) => current + 1);
      } catch (error) {
        console.error("Failed to allocate accommodation room:", error);
        toast.error(
          getApiErrorMessage(error, "Failed to allocate the selected room"),
        );
        return false;
      }
    }

    const movedApplicants = movingApplicants.map((item) => ({
      ...item,
      roomAssigned: roomId,
    }));
    const movedById = new Map(movedApplicants.map((item) => [item.id, item]));

    setTours((current) =>
      current.map((tour) =>
        tour.id !== assignment.tourId
          ? tour
          : {
              ...tour,
              clubbedFamilies: tour.clubbedFamilies.map((item) => ({
                ...item,
                members: item.members.map(
                  (member) => movedById.get(member.id) ?? member,
                ),
              })),
              nonClubbedApplicants: tour.nonClubbedApplicants.map(
                (member) => movedById.get(member.id) ?? member,
              ),
            },
      ),
    );

    setProperties((current) =>
      current.map((property) => ({
        ...property,
        rooms: property.rooms.map((room) => {
          const shouldRemoveMovedApplicants = assignmentSlotKey
            ? room.id === previousRequestRoomId
            : true;
          const occupantsWithoutMoved = shouldRemoveMovedApplicants
            ? (room.occupants ?? []).filter((item) => !movingIds.has(item.id))
            : (room.occupants ?? []);
          const isTarget = property.id === propertyId && room.id === roomId;
          const occupants = isTarget
            ? [...occupantsWithoutMoved, ...movedApplicants]
            : occupantsWithoutMoved;
          const occupied = occupants.length;
          const status: RoomStatus =
            room.status === "maintenance" || room.status === "blocked"
              ? room.status
              : occupied === 0
                ? "available"
                : occupied >= room.capacity
                  ? "occupied"
                  : "partial";
          const familyId =
            isTarget && family
              ? family.id
              : occupants.length === 0
                ? undefined
                : room.familyId;
          return { ...room, occupants, occupied, status, familyId };
        }),
      })),
    );

    if (assignmentSlotKey) {
      setRequestRoomAssignments((current) => ({
        ...current,
        [assignmentSlotKey]: roomId,
      }));
      setLocallyUnlinkedRequestSlots((current) => {
        const next = new Set(current);
        next.delete(assignmentSlotKey);
        return next;
      });
    }

    const localSuccessMessage = family
      ? `${family.familyCode} assigned to ${targetProperty.name} ${targetRoom.number}`
      : applicant
        ? `${applicant.name} assigned to ${targetProperty.name} ${targetRoom.number}`
        : movingApplicants.length
          ? `${movingApplicants.length} applicant(s) assigned to ${targetProperty.name} ${targetRoom.number}`
          : `${targetProperty.name} ${targetRoom.number} assigned to the room request`;
    toast.success(apiSuccessMessage || localSuccessMessage);
    return true;
  };

  const selectedRoomRequest = roomRequests.find(
    (request) => request.id === selectedRoomRequestId,
  );

  const selectedRequestTour = selectedRoomRequest
    ? tours.find((tour) => tour.id === selectedRoomRequest.tourId)
    : undefined;
  const getApplicantsForRequestRoom = (
    request: TourRoomRequest,
    tour: Tour,
    roomIndex: number,
  ) => {
    const requestIds = new Set(request.applicantIds);
    const allApplicants = [
      ...tour.clubbedFamilies.flatMap((family) => family.members),
      ...tour.nonClubbedApplicants,
    ].filter((applicant) => requestIds.has(applicant.id));
    const roomCount = Math.max(1, request.roomsRequested);
    const baseSize = Math.floor(allApplicants.length / roomCount);
    const remainder = allApplicants.length % roomCount;
    const start = roomIndex * baseSize + Math.min(roomIndex, remainder);
    const count = baseSize + (roomIndex < remainder ? 1 : 0);
    return allApplicants.slice(start, start + count);
  };

  const getAssignedRoomIdForRequestSlot = (
    request: TourRoomRequest,
    roomIndex: number,
  ) => {
    const slotKey = requestSlotKey(request.id, roomIndex);
    const locallyAssignedRoomId = requestRoomAssignments[slotKey];
    if (
      locallyAssignedRoomId &&
      properties.some((property) =>
        property.rooms.some((room) => room.id === locallyAssignedRoomId),
      )
    ) {
      return locallyAssignedRoomId;
    }

    if (locallyUnlinkedRequestSlots.has(slotKey)) return undefined;
    if (roomIndex !== 0 || !request.assignedRoomNumber) return undefined;

    const assignedPropertyRoomTypeId = request.assignedPropertyRoomTypeId ?? request.propertyRoomTypeId;
    return properties
      .find((property) => property.id === (request.assignedPropertyId ?? request.propertyId))
      ?.rooms.find(
        (room) =>
          roomNumberKey(room.number) ===
            roomNumberKey(request.assignedRoomNumber) &&
          (assignedPropertyRoomTypeId === undefined
            ? normalizedRoomType(room.type) ===
              normalizedRoomType(request.roomType)
            : room.propertyRoomTypeId === assignedPropertyRoomTypeId),
      )?.id;
  };

  const allocatedRequestSlotsByRoomId = new Map<
    string,
    AllocatedRoomRequestSlot[]
  >();
  roomRequests.forEach((request) => {
    Array.from({ length: Math.max(1, request.roomsRequested) }).forEach(
      (_, roomIndex) => {
        const roomId = getAssignedRoomIdForRequestSlot(request, roomIndex);
        if (!roomId) return;
        const slots = allocatedRequestSlotsByRoomId.get(roomId) ?? [];
        slots.push({ request, roomIndex });
        allocatedRequestSlotsByRoomId.set(roomId, slots);
      },
    );
  });

  useEffect(() => {
    setRequestRoomAssignments((current) => {
      if (Object.keys(current).length) return current;
      const initial: Record<string, string> = {};
      roomRequests.forEach((request) => {
        const tour = tours.find((item) => item.id === request.tourId);
        if (!tour) return;
        Array.from({ length: Math.max(1, request.roomsRequested) }).forEach(
          (_, roomIndex) => {
            const applicants = getApplicantsForRequestRoom(
              request,
              tour,
              roomIndex,
            );
            const roomIds = new Set(
              applicants
                .map((applicant) => applicant.roomAssigned)
                .filter((roomId): roomId is string => Boolean(roomId)),
            );
            if (!applicants.length || roomIds.size !== 1) return;
            const roomId = Array.from(roomIds)[0];
            const property = properties.find((item) =>
              item.rooms.some((room) => room.id === roomId),
            );
            const requestVenue = request.venue
              .toLowerCase()
              .replace("kerbala", "karbala");
            const propertyVenue = property?.location
              .toLowerCase()
              .replace("kerbala", "karbala");
            if (requestVenue === propertyVenue) {
              initial[requestSlotKey(request.id, roomIndex)] = roomId;
            }
          },
        );
      });
      return initial;
    });
  }, [properties, roomRequests, tours]);
  const selectedRequestApplicants =
    selectedRequestTour && selectedRoomRequest
      ? getApplicantsForRequestRoom(
          selectedRoomRequest,
          selectedRequestTour,
          selectedRequestRoomIndex,
        )
      : [];
  const selectedRequestAssignedRoomId = selectedRoomRequest
    ? getAssignedRoomIdForRequestSlot(
        selectedRoomRequest,
        selectedRequestRoomIndex,
      )
    : undefined;
  const unassignedRequestApplicants = selectedRequestApplicants.filter(
    (applicant) => !applicant.roomAssigned,
  );
  const searchedRequestApplicants = unassignedRequestApplicants.filter(
    (applicant) => {
      const query = tourApplicantSearch.trim().toLowerCase();
      return (
        !query ||
        applicant.name.toLowerCase().includes(query) ||
        applicant.itsId.includes(query)
      );
    },
  );

  const getTourRequestStatus = (
    tourId: string,
  ): "completed" | "partial" | "pending" => {
    const tourRequests = roomRequests.filter(
      (request) => request.tourId === tourId,
    );
    const slots = tourRequests.flatMap((request) =>
      Array.from(
        { length: Math.max(1, request.roomsRequested) },
        (_, roomIndex) => requestSlotKey(request.id, roomIndex),
      ),
    );
    const completedFromApi = tourRequests.flatMap((request) =>
      Array.from({ length: Math.max(1, request.roomsRequested) }).filter(
        (_, roomIndex) =>
          request.status?.toLowerCase().startsWith("complet") &&
          !locallyUnlinkedRequestSlots.has(
            requestSlotKey(request.id, roomIndex),
          ),
      ),
    ).length;
    const assigned = slots.filter((key) => requestRoomAssignments[key]).length;
    const completed = Math.max(assigned, completedFromApi);
    if (slots.length > 0 && completed === slots.length) return "completed";
    if (completed > 0) return "partial";
    return "pending";
  };
  const normalizedVenueFilter = venueFilter
    .toLowerCase()
    .replace("kerbala", "karbala");
  const visibleRoomRequests = roomRequests.filter((request) => {
    const requestVenue = request.venue
      .toLowerCase()
      .replace("kerbala", "karbala");
    if (venueFilter !== "all" && requestVenue !== normalizedVenueFilter)
      return false;
    if (
      statusFilter !== "all" &&
      request.tourId !== selectedRoomRequest?.tourId &&
      getTourRequestStatus(request.tourId) !== statusFilter
    )
      return false;
    return true;
  });
  const requestTourIds = Array.from(
    new Set(visibleRoomRequests.map((request) => request.tourId)),
  );
  const searchedRequestTourIds = requestTourIds.filter((tourId) => {
    const query = tourDropdownSearch.trim().toLowerCase();
    if (!query) return true;
    const tour = tours.find((item) => item.id === tourId);
    const requests = visibleRoomRequests.filter(
      (request) => request.tourId === tourId,
    );
    const tourApplicants = tour
      ? [
          ...tour.clubbedFamilies.flatMap((family) => family.members),
          ...tour.nonClubbedApplicants,
        ]
      : [];
    return Boolean(
      tour?.name.toLowerCase().includes(query) ||
        requests.some((request) =>
          request.requestedBy.toLowerCase().includes(query),
        ) ||
        tourApplicants.some(
          (applicant) =>
            applicant.name.toLowerCase().includes(query) ||
            applicant.itsId.toLowerCase().includes(query),
        ),
    );
  });
  const selectedTourRequests = selectedRoomRequest
    ? visibleRoomRequests.filter(
        (request) => request.tourId === selectedRoomRequest.tourId,
      )
    : [];
  const firstVisibleRequestId = visibleRoomRequests[0]?.id ?? "";
  const selectedRequestIsVisible = visibleRoomRequests.some(
    (request) => request.id === selectedRoomRequestId,
  );

  useEffect(() => {
    if (!firstVisibleRequestId) {
      if (selectedRoomRequestId) setSelectedRoomRequestId("");
      setSelectedRequestRoomType("");
      setSelectedRequestPropertyId("");
      setSelectedRequestPropertyRoomTypeId(undefined);
      return;
    }
    if (!selectedRequestIsVisible) {
      const firstRequest = roomRequests.find(
        (request) => request.id === firstVisibleRequestId,
      );
      const firstTour = firstRequest
        ? tours.find((tour) => tour.id === firstRequest.tourId)
        : undefined;
      const firstRequestAssigned = Boolean(
        firstRequest?.assignedRoomNumber ||
          firstRequest?.status?.toLowerCase().startsWith("complet"),
      );
      const firstApplicantIds =
        firstRequest && firstTour && !firstRequestAssigned
          ? getApplicantsForRequestRoom(firstRequest, firstTour, 0)
              .filter((applicant) => !applicant.roomAssigned)
              .map((applicant) => applicant.id)
          : [];
      setSelectedRoomRequestId(firstVisibleRequestId);
      setSelectedRequestRoomType(firstRequest?.roomType ?? "");
      setSelectedRequestPropertyId(firstRequest?.propertyId ?? "");
      setSelectedRequestPropertyRoomTypeId(firstRequest?.propertyRoomTypeId);
      setSelectedRequestRoomIndex(0);
      setSelectedRequestApplicantIds(new Set(firstApplicantIds));
      setSelectedAllocationRoomId("");
    }
  }, [
    firstVisibleRequestId,
    roomRequests,
    selectedRequestIsVisible,
    selectedRoomRequestId,
    tours,
  ]);

  const allTourApplicants = tours.flatMap((tour) => [
    ...tour.clubbedFamilies.flatMap((family) => family.members),
    ...tour.nonClubbedApplicants,
  ]);
  const getAssignedApplicantsForRoom = (room: Room) => {
    const requestApplicants = (
      allocatedRequestSlotsByRoomId.get(room.id) ?? []
    ).flatMap(({ request, roomIndex }) => {
      const tour = tours.find((item) => item.id === request.tourId);
      if (!tour) return [];
      return getApplicantsForRequestRoom(request, tour, roomIndex).map(
        (applicant) => ({ ...applicant, roomAssigned: room.id }),
      );
    });
    const roomKeys = new Set([
      room.id.replace("#", ""),
      room.number.replace("#", ""),
    ]);
    return Array.from(
      new Map<string, Applicant>(
        [
          ...(room.occupants ?? []),
          ...requestApplicants,
          ...allTourApplicants.filter(
            (applicant) =>
              applicant.roomAssigned &&
              roomKeys.has(applicant.roomAssigned.replace("#", "")),
          ),
        ].map((applicant) => [applicant.id, applicant]),
      ).values(),
    );
  };

  const selectedInventoryTourId = selectedRoomRequest?.tourId;
  const selectedInventoryTour = selectedInventoryTourId
    ? tours.find((tour) => tour.id === selectedInventoryTourId)
    : undefined;
  const selectedInventoryTourApplicantIds = new Set(
    selectedInventoryTour
      ? [
          ...selectedInventoryTour.clubbedFamilies.flatMap(
            (family) => family.members,
          ),
          ...selectedInventoryTour.nonClubbedApplicants,
        ].map((applicant) => applicant.id)
      : [],
  );
  const selectedTourHasUnassignedRequest = selectedInventoryTourId
    ? roomRequests
        .filter((request) => request.tourId === selectedInventoryTourId)
        .some((request) =>
          Array.from(
            { length: Math.max(1, request.roomsRequested) },
            (_, roomIndex) =>
              !requestRoomAssignments[requestSlotKey(request.id, roomIndex)],
          ).some(Boolean),
        )
    : false;
  const getRoomTourScope = (room: Room) => {
    const occupants = getAssignedApplicantsForRoom(room);
    const allocatedSlots = allocatedRequestSlotsByRoomId.get(room.id) ?? [];
    const belongsToSelectedTour =
      occupants.some((applicant) =>
        selectedInventoryTourApplicantIds.has(applicant.id),
      ) ||
      allocatedSlots.some(
        ({ request }) => request.tourId === selectedInventoryTourId,
      );
    return {
      occupants,
      belongsToSelectedTour,
      isAvailable:
        allocatedSlots.length === 0 &&
        occupants.length === 0 &&
        room.status === "available",
    };
  };

  /* filter properties for left panel */
  const selectedTourVenues = new Set(
    selectedTourRequests.map((request) =>
      request.venue.toLowerCase().replace("kerbala", "karbala"),
    ),
  );
  const selectedVenue =
    venueFilter !== "all" ? normalizedVenueFilter : undefined;
  const requestedPropertyIds = new Set(
    selectedTourRequests.map((request) => request.propertyId),
  );
  const requestedPropertyOrder = new Map<string, number>();
  selectedTourRequests.forEach((request) => {
    if (!requestedPropertyOrder.has(request.propertyId)) {
      requestedPropertyOrder.set(
        request.propertyId,
        requestedPropertyOrder.size,
      );
    }
  });
  const filteredProperties = properties.filter((p) => {
    const propertyVenue = p.location
      .toLowerCase()
      .replace("kerbala", "karbala");
    if (selectedVenue && propertyVenue !== selectedVenue) return false;
    if (selectedRequestPropertyId && p.id !== selectedRequestPropertyId)
      return false;
    if (
      selectedInventoryTourId &&
      requestedPropertyIds.size > 0 &&
      !requestedPropertyIds.has(p.id)
    )
      return false;
    if (
      !selectedVenue &&
      selectedInventoryTourId &&
      selectedTourVenues.size > 0 &&
      !selectedTourVenues.has(propertyVenue)
    )
      return false;
    if (
      search &&
      !p.name.toLowerCase().includes(search.toLowerCase()) &&
      !p.location.toLowerCase().includes(search.toLowerCase())
    )
      return false;
    return true;
  });

  const roomMatchesOccupancyWindow = (room: Room) => {
    if (
      !showNonAvailableRooms &&
      (room.status !== "available" ||
        getAssignedApplicantsForRoom(room).length !== 0 ||
        allocatedRequestSlotsByRoomId.has(room.id))
    )
      return false;
    if (!TEST_DATA_ALWAYS_AVAILABLE) {
      if (dateFrom || dateTo) {
        if (!room.arrivalDate) return false;
        const arrival = new Date(room.arrivalDate);
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        if ((from && arrival < from) || (to && arrival > to)) return false;
      }
    }
    return true;
  };

  const getRooms = (property: Property) =>
    property.rooms.filter((room) => {
      if (!roomMatchesOccupancyWindow(room)) return false;
      if (
        !showNonRelatedRooms &&
        selectedRequestPropertyRoomTypeId !== undefined &&
        room.propertyRoomTypeId !== selectedRequestPropertyRoomTypeId
      )
        return false;
      if (
        !showNonRelatedRooms &&
        (selectedRequestRoomType || selectedRoomRequest?.roomType) &&
        normalizedRoomType(room.type) !==
          normalizedRoomType(
            selectedRequestRoomType || selectedRoomRequest?.roomType || "",
          )
      )
        return false;
      if (selectedInventoryTourId) {
        if (showNonAvailableRooms) return true;
        const scope = getRoomTourScope(room);
        if (scope.belongsToSelectedTour) return true;
        if (
          selectedTourHasUnassignedRequest &&
          (scope.isAvailable ||
            room.status === "maintenance" ||
            room.status === "blocked")
        )
          return true;
        return false;
      }
      return true;
    });
  const visibleProperties = filteredProperties
    .filter(
      (property) =>
        (!selectedInventoryTourId && showNonAvailableRooms) ||
        getRooms(property).length > 0,
    )
    .sort((left, right) => {
      const leftOrder = requestedPropertyOrder.get(left.id);
      const rightOrder = requestedPropertyOrder.get(right.id);

      if (leftOrder === undefined && rightOrder === undefined) return 0;
      if (leftOrder === undefined) return 1;
      if (rightOrder === undefined) return -1;
      return leftOrder - rightOrder;
    });
  const selectableRequestRooms = visibleProperties.flatMap((property) =>
    property.rooms
      .filter(
        (room) =>
          room.id !== selectedRequestAssignedRoomId &&
          !allocatedRequestSlotsByRoomId.has(room.id) &&
          roomMatchesOccupancyWindow(room) &&
          (showNonRelatedRooms ||
            ((!selectedRequestPropertyRoomTypeId ||
              room.propertyRoomTypeId === selectedRequestPropertyRoomTypeId) &&
              normalizedRoomType(room.type) ===
                normalizedRoomType(
                  selectedRequestRoomType ||
                    selectedRoomRequest?.roomType ||
                    room.type,
                ))) &&
          room.status !== "maintenance" &&
          room.status !== "blocked" &&
          room.occupied < room.capacity &&
          (!selectedInventoryTourId ||
            getRoomTourScope(room).belongsToSelectedTour ||
            getRoomTourScope(room).isAvailable),
      )
      .map((room) => ({ property, room })),
  );
  const selectedAllocationTarget = selectableRequestRooms.find(
    ({ room }) => room.id === selectedAllocationRoomId,
  );

  const filteredTours = tours.filter((tour) => {
    if (TEST_DATA_ALWAYS_AVAILABLE) return true;
    const arrival = new Date(tour.startDate);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    return (!from || arrival >= from) && (!to || arrival <= to);
  });
  const tourApplicantQuery = tourApplicantSearch.trim().toLowerCase();
  const rightPanelTours = filteredTours.filter((tour) => {
    if (selectedRoomRequest && tour.id !== selectedRoomRequest.tourId)
      return false;
    if (!tourApplicantQuery) return true;
    if (tour.name.toLowerCase().includes(tourApplicantQuery)) return true;
    return [
      ...tour.clubbedFamilies.flatMap((family) => family.members),
      ...tour.nonClubbedApplicants,
    ].some(
      (applicant) =>
        applicant.name.toLowerCase().includes(tourApplicantQuery) ||
        applicant.itsId.includes(tourApplicantQuery),
    );
  });
  const availableTargetRooms =
    properties
      .find((property) => property.id === targetPropertyId)
      ?.rooms.filter(
        (room) =>
          room.status !== "maintenance" &&
          room.status !== "blocked" &&
          room.occupied < room.capacity,
      ) || [];
  const selectedTargetRoom = availableTargetRooms.find(
    (room) => room.id === targetRoomId,
  );
  const roomReallocationRequest = roomReallocationTarget?.request;

  const roomReallocationTour = roomReallocationRequest
    ? tours.find((tour) => tour.id === roomReallocationRequest.tourId)
    : undefined;

  const roomReallocationOccupants =
    roomReallocationTarget && roomReallocationTour
      ? getApplicantsForRequestRoom(
          roomReallocationTarget.request,
          roomReallocationTour,
          roomReallocationTarget.roomIndex,
        ).map((applicant) => ({
          ...applicant,
          roomAssigned: roomReallocationTarget.room.id,
        }))
      : (roomReallocationTarget?.room.occupants ?? []);
  const roomReallocationProperty = roomReallocationTarget
    ? properties.find(
        (property) => property.id === roomReallocationTarget.propertyId,
      )
    : undefined;
  const applicantReallocationTour = reallocationTarget
    ? tours.find((tour) => tour.id === reallocationTarget.tourId)
    : undefined;
  const applicantReallocationRequest = reallocationTarget
    ? roomRequests.find(
        (request) =>
          request.tourId === reallocationTarget.tourId &&
          request.applicantIds.includes(reallocationTarget.applicant.id),
      )
    : undefined;
  const toggleRequestSlot = (request: TourRoomRequest, roomIndex: number) => {
    const tour = tours.find((item) => item.id === request.tourId);
    const slotAssigned = Boolean(
      request.assignedRoomNumber ||
        request.status?.toLowerCase().startsWith("complet") ||
        requestRoomAssignments[requestSlotKey(request.id, roomIndex)],
    );
    const unassignedApplicantIds =
      tour && !slotAssigned
        ? getApplicantsForRequestRoom(request, tour, roomIndex).map(
            (applicant) => applicant.id,
          )
        : [];
    setSelectedRoomRequestId(request.id);
    setSelectedRequestRoomType(request.roomType);
    setSelectedRequestPropertyId(request.propertyId);
    setSelectedRequestPropertyRoomTypeId(request.propertyRoomTypeId);
    setSelectedRequestRoomIndex(roomIndex);
    setSelectedRequestApplicantIds(new Set(unassignedApplicantIds));
    setSelectedAllocationRoomId("");
    setShowNonRelatedRooms(false);
  };

  const unlinkRequestSlotLocally = (
    request: TourRoomRequest,
    roomIndex: number,
  ) => {
    const slotKey = requestSlotKey(request.id, roomIndex);
    const tour = tours.find((item) => item.id === request.tourId);
    const applicantIds = new Set(
      tour
        ? getApplicantsForRequestRoom(request, tour, roomIndex).map(
            (applicant) => applicant.id,
          )
        : [],
    );

    setRequestRoomAssignments((current) => {
      const next = { ...current };
      delete next[slotKey];
      return next;
    });
    setLocallyUnlinkedRequestSlots((current) => {
      const next = new Set(current);
      next.add(slotKey);
      return next;
    });

    if (applicantIds.size) {
      setTours((current) =>
        current.map((item) =>
          item.id !== request.tourId
            ? item
            : {
                ...item,
                clubbedFamilies: item.clubbedFamilies.map((family) => ({
                  ...family,
                  members: family.members.map((member) =>
                    applicantIds.has(member.id)
                      ? { ...member, roomAssigned: undefined }
                      : member,
                  ),
                })),
                nonClubbedApplicants: item.nonClubbedApplicants.map((member) =>
                  applicantIds.has(member.id)
                    ? { ...member, roomAssigned: undefined }
                    : member,
                ),
              },
        ),
      );
      setProperties((current) =>
        current.map((property) => ({
          ...property,
          rooms: property.rooms.map((room) => {
            const occupants = (room.occupants ?? []).filter(
              (occupant) => !applicantIds.has(occupant.id),
            );
            const occupied = occupants.length;
            const status: RoomStatus =
              room.status === "maintenance" || room.status === "blocked"
                ? room.status
                : occupied === 0
                  ? "available"
                  : occupied >= room.capacity
                    ? "occupied"
                    : "partial";
            return { ...room, occupants, occupied, status };
          }),
        })),
      );
    }

    toggleRequestSlot(request, roomIndex);
    setSelectedRequestApplicantIds(new Set(applicantIds));
  };

  // UNALLOCATE ROOM FROM SELECTED ROOM REQUEST
  const confirmRoomUnallocation = async () => {
    const target = roomUnallocationTarget;
    if (!target || roomUnallocationSaving) return;

    if (!target.request.roomAllocationPublicId) {
      toast.error("Room allocation ID is missing from this request");
      setRoomUnallocationTarget(null);
      return;
    }

    try {
      setRoomUnallocationSaving(true);
      const response = await unallocateAccommodationRoom(
        target.request.roomAllocationPublicId,
      );
      if (!response.success) {
        toast.error(response.message || "Failed to unallocate the room");
        return;
      }

      unlinkRequestSlotLocally(target.request, target.roomIndex);
      setRoomUnallocationTarget(null);
      toast.success(response.message || "Room unallocated successfully");
      setDashboardRefreshVersion((current) => current + 1);
    } catch (error) {
      console.error("Failed to unallocate accommodation room:", error);
      toast.error(getApiErrorMessage(error, "Failed to unallocate the room"));
    } finally {
      setRoomUnallocationSaving(false);
    }
  };

  // ASSIGN SELECTED ROOM REQUEST APPLICANTS TO SELECTED ROOM
  const assignSelectedApplicants = async () => {
    if (
      !selectedRoomRequest ||
      !selectedRequestTour ||
      !selectedAllocationTarget
    ) {
      toast.error("Open a room request and select a compatible room number");
      return;
    }

    setRoomAssignmentSaving(true);
    try {
      const assigned = await dropAssignmentOnRoom(
        {
          kind: "group",
          tourId: selectedRequestTour.id,
          applicantIds: selectedRequestApplicants.map(
            (applicant) => applicant.id,
          ),
          requestId: selectedRoomRequest.id,
          roomIndex: selectedRequestRoomIndex,
        },
        selectedAllocationTarget.property.id,
        selectedAllocationTarget.room.id,
      );
      if (!assigned) return;

      setSelectedRequestApplicantIds(new Set());
      setSelectedAllocationRoomId("");
    } finally {
      setRoomAssignmentSaving(false);
    }
  };

  const openAutoAllocationConfirmation = () => {
    if (!selectedRequestTour?.id) {
      toast.error("Select a tour before auto-allocating rooms");
      return;
    }

    setAutoAllocationTarget({
      tourPublicId: selectedRequestTour.id,
      tourName: selectedRequestTour.name,
      tourCode: selectedRequestTour.tourCode ?? "N/A",
    });
  };

  const confirmAutoAllocation = async () => {
    const target = autoAllocationTarget;
    if (!target || roomAssignmentSaving) return;

    setRoomAssignmentSaving(true);
    try {
      const response = await autoAssignAccommodationRooms(target.tourPublicId);
      if (response.success === false) {
        toast.error(response.message || "Failed to auto-allocate rooms");
        return;
      }

      toast.success(response.message || "Rooms auto-allocated successfully");
      setAutoAllocationTarget(null);
      setSelectedRequestApplicantIds(new Set());
      setSelectedAllocationRoomId("");
      setDashboardRefreshVersion((current) => current + 1);
    } catch (error) {
      console.error("Failed to auto-allocate accommodation rooms:", error);
      toast.error(getApiErrorMessage(error, "Failed to auto-allocate rooms"));
    } finally {
      setRoomAssignmentSaving(false);
    }
  };

  const handleAutoAllocationDialogChange = (open: boolean) => {
    if (roomAssignmentSaving) return;
    if (!open) setAutoAllocationTarget(null);
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 50%, #f0f7ff 100%)",
      }}
    >
      {dashboardLoading && <FullScreenLoading />}
      {/*
       SECTION */}
      <div className="space-y-3 pb-4 pt-1">
        {/* ── Filters ── */}
        <Card className="overflow-visible rounded-2xl border-sky-200/70 bg-white/95 shadow-[0_10px_30px_rgba(15,70,120,0.10)]">
          <CardContent className="p-4">
            <div
              className={`flex flex-wrap items-center justify-between gap-3 ${filtersExpanded ? "mb-3 border-b border-sky-100 pb-2.5" : ""}`}
            >
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                  <SlidersHorizontal className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Find Rooms & Allocations
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Filter availability by tour, location and travel window
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDateFrom(TODAY);
                    setDateTo("");
                    setVenueFilter("all");
                    setStatusFilter("all");
                    setSearch("");
                    setShowNonAvailableRooms(false);
                    setShowNonRelatedRooms(false);
                  }}
                  className="h-8 text-xs text-sky-700"
                >
                  Reset filters
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFiltersExpanded((current) => !current)}
                  className="h-8 w-8 text-sky-700"
                  title={
                    filtersExpanded ? "Collapse filters" : "Expand filters"
                  }
                >
                  {filtersExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            {filtersExpanded && (
              <>
                <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-2 lg:grid-cols-[160px_160px_160px_160px_minmax(220px,1fr)]">
                  <div>
                    <div className="text-[10px] font-medium text-green-600 mb-1 flex items-center gap-0.5">
                      <CalendarDays className="h-2.5 w-2.5" />
                      From
                    </div>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="h-8 text-xs w-full border-green-300 focus-visible:ring-green-300"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-medium text-red-500 mb-1 flex items-center gap-0.5">
                      <CalendarDays className="h-2.5 w-2.5" />
                      To
                    </div>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="h-8 text-xs w-full border-red-300 focus-visible:ring-red-300"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-medium text-amber-700 mb-1 flex items-center gap-0.5">
                      <MapPin className="h-2.5 w-2.5" />
                      Venue
                    </div>
                    <Select
                      value={venueFilter}
                      onValueChange={setVenueFilter}
                      disabled={venuesLoading}
                    >
                      <SelectTrigger className="h-8 w-full border-amber-200 text-xs">
                        <SelectValue
                          placeholder={
                            venuesLoading ? "Loading venues..." : "All Venues"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Venues</SelectItem>
                        {venues.map((venue) => (
                          <SelectItem
                            key={venue.venueId}
                            value={venue.venueName.toLowerCase()}
                          >
                            {venue.venueName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-[10px] font-medium text-sky-700 mb-1">
                      Status
                    </div>
                    <Select
                      value={statusFilter}
                      onValueChange={setStatusFilter}
                    >
                      <SelectTrigger className="h-8 text-xs w-full border-sky-200">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="pending">Unassigned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-medium text-sky-700 mb-1">
                      Search
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search property, city, pax name or ITS…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-8 text-xs pl-7 w-full border-sky-200"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg bg-slate-50 px-3 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    Today's movement
                  </span>
                  <span className="text-xs text-emerald-700">
                    <strong className="text-sm">
                      {todayMovement.checkIns}
                    </strong>{" "}
                    check-ins
                  </span>
                  <span className="text-xs text-rose-600">
                    <strong className="text-sm">
                      {todayMovement.checkOuts}
                    </strong>{" "}
                    check-outs
                  </span>
                  <span className="text-xs text-sky-700">
                    <strong className="text-sm">
                      {todayMovement.occupancyPercentage}%
                    </strong>{" "}
                    occupied
                  </span>
                  <label className="ml-auto flex cursor-pointer items-center gap-2 rounded-md border border-emerald-200 bg-white px-2.5 py-1 text-[10px] font-medium text-emerald-700 shadow-sm">
                    <input
                      type="checkbox"
                      checked={showNonAvailableRooms}
                      onChange={(event) =>
                        setShowNonAvailableRooms(event.target.checked)
                      }
                      className="h-3.5 w-3.5 accent-emerald-600"
                    />
                    Show non available rooms
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-sky-200 bg-white px-2.5 py-1 text-[10px] font-medium text-sky-700 shadow-sm">
                    <input
                      type="checkbox"
                      checked={showNonRelatedRooms}
                      onChange={(event) =>
                        setShowNonRelatedRooms(event.target.checked)
                      }
                      className="h-3.5 w-3.5 accent-sky-600"
                    />
                    Show Non related rooms
                  </label>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Compact tour request accordion */}
        <Card
          className={`relative overflow-visible rounded-2xl border-sky-200/70 bg-white shadow-[0_8px_24px_rgba(15,70,120,0.08)] ${tourDropdownOpen ? "z-40" : "z-10"}`}
        >
          <CardContent className="p-0">
            <div className="flex items-center gap-2 rounded-t-2xl border-b border-sky-100 bg-sky-50 px-4 py-3">
              <ClipboardList className="h-4 w-4 text-sky-700" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900">
                  Tour Room Requests
                </div>
                <div className="text-[10px] text-slate-500">
                  Select a tour, review its requested room types and allocate
                  applicants
                </div>
              </div>
              <Badge className="shrink-0 border-sky-200 bg-white text-[9px] text-sky-700">
                {dashboardLoading
                  ? "Loading…"
                  : `${requestTourIds.length} tours`}
              </Badge>
            </div>
            <div className="border-b border-sky-100 bg-white px-4 py-3">
              <div className="mb-1 text-[10px] font-semibold text-slate-700">
                Select Tour
              </div>
              <div ref={tourDropdownRef} className="relative w-full">
                <button
                  type="button"
                  onClick={() => setTourDropdownOpen((current) => !current)}
                  className={`grid h-10 w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border bg-white px-3 text-left transition-colors sm:grid-cols-[minmax(0,2fr)_minmax(0,1.25fr)_minmax(90px,0.65fr)_minmax(160px,1.15fr)_auto] ${tourDropdownOpen ? "border-sky-400 ring-2 ring-sky-100" : "border-sky-200 hover:border-sky-300"}`}
                >
                  {selectedRequestTour ? (
                    <>
                      <span className="truncate text-xs font-semibold text-slate-900">
                        {selectedRequestTour.name}
                      </span>
                      <span className="hidden truncate text-[10px] text-slate-500 sm:block">
                        {selectedTourRequests[0]?.requestedBy}
                      </span>
                      <span className="hidden text-[10px] text-slate-500 sm:block">
                        {
                          new Set(
                            selectedTourRequests.flatMap(
                              (request) => request.applicantIds,
                            ),
                          ).size
                        }{" "}
                        applicants
                      </span>
                      <span className="hidden items-center gap-1 text-[10px] text-slate-500 sm:flex">
                        <Calendar className="h-3 w-3" />
                        {fmt(selectedRequestTour.startDate)} →{" "}
                        {fmt(selectedRequestTour.endDate)}
                      </span>
                      <span className="flex items-center justify-end gap-2">
                        <Badge className="hidden border-sky-200 bg-sky-50 px-1.5 py-0 text-[8px] text-sky-700 md:inline-flex">
                          {TOUR_CFG[selectedRequestTour.tourType].label}
                        </Badge>
                        <ChevronDown
                          className={`h-4 w-4 text-slate-400 transition-transform ${tourDropdownOpen ? "rotate-180" : ""}`}
                        />
                      </span>
                    </>
                  ) : (
                    <span className="col-span-full flex items-center justify-between text-xs text-slate-400">
                      Select a tour to view room requests
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  )}
                </button>
                {tourDropdownOpen && (
                  <div className="absolute inset-x-0 top-full z-[60] mt-1 w-full overflow-hidden rounded-xl border border-sky-300 bg-white shadow-2xl">
                    <div className="border-b border-slate-100 p-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                        <Input
                          autoFocus
                          value={tourDropdownSearch}
                          onChange={(event) =>
                            setTourDropdownSearch(event.target.value)
                          }
                          onClick={(event) => event.stopPropagation()}
                          placeholder="Search tour, Tour Operator, pax name or ITS…"
                          className="h-8 border-sky-200 pl-8 text-xs"
                        />
                      </div>
                    </div>
                    <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,1.25fr)_minmax(90px,0.65fr)_minmax(160px,1.15fr)_minmax(70px,0.5fr)_minmax(90px,0.65fr)] gap-3 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[8px] font-bold uppercase tracking-wide text-slate-600 sm:grid">
                      <span>Tour</span>
                      <span>Tour operator</span>
                      <span>Applicants</span>
                      <span>Travel dates</span>
                      <span>Type</span>
                      <span>Status</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-1">
                      {searchedRequestTourIds.map((tourId) => {
                        const tour = tours.find((item) => item.id === tourId);
                        const requests = visibleRoomRequests.filter(
                          (request) => request.tourId === tourId,
                        );
                        const applicantIds = new Set(
                          requests.flatMap((request) => request.applicantIds),
                        );
                        const operator =
                          requests[0]?.requestedBy ?? "Tour Operator";
                        const active = selectedRoomRequest?.tourId === tourId;
                        const query = tourDropdownSearch.trim().toLowerCase();
                        const tourApplicants = tour
                          ? [
                              ...tour.clubbedFamilies.flatMap(
                                (family) => family.members,
                              ),
                              ...tour.nonClubbedApplicants,
                            ]
                          : [];
                        const matchingRoomNumbers = Array.from(
                          new Set(
                            tourApplicants
                              .filter(
                                (applicant) =>
                                  Boolean(query) &&
                                  (applicant.name
                                    .toLowerCase()
                                    .includes(query) ||
                                    applicant.itsId
                                      .toLowerCase()
                                      .includes(query)),
                              )
                              .map((applicant) => applicant.roomAssigned)
                              .filter((room): room is string => Boolean(room)),
                          ),
                        );
                        const requestStatus = getTourRequestStatus(tourId);
                        return (
                          <button
                            key={tourId}
                            type="button"
                            onClick={() => {
                              const firstRequest = requests[0];
                              if (firstRequest)
                                toggleRequestSlot(firstRequest, 0);
                              setSelectedRequestRoomIndex(0);
                              setSelectedAllocationRoomId("");
                              setTourApplicantSearch("");
                              setTourDropdownSearch("");
                              setTourDropdownOpen(false);
                            }}
                            className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-2.5 py-2 text-left sm:grid-cols-[minmax(0,2fr)_minmax(0,1.25fr)_minmax(90px,0.65fr)_minmax(160px,1.15fr)_minmax(70px,0.5fr)_minmax(90px,0.65fr)] ${active ? "bg-sky-50 text-sky-900" : "text-slate-700 hover:bg-slate-50"}`}
                          >
                            <span className="min-w-0">
                              <span className="block truncate text-[11px] font-semibold">
                                {tour?.name}
                              </span>
                              {matchingRoomNumbers.length > 0 && (
                                <span className="block text-[8px] font-medium text-sky-700">
                                  Room{" "}
                                  {matchingRoomNumbers
                                    .map((room) => `#${room.replace("#", "")}`)
                                    .join(", ")}
                                </span>
                              )}
                            </span>
                            <span className="hidden truncate text-[9px] text-slate-500 sm:block">
                              {operator}
                            </span>
                            <span className="hidden text-[9px] text-slate-500 sm:block">
                              {applicantIds.size} applicants
                            </span>
                            <span className="hidden items-center gap-1 text-[9px] text-slate-500 sm:flex">
                              <Calendar className="h-3 w-3 shrink-0" />
                              {tour
                                ? `${fmt(tour.startDate)} → ${fmt(tour.endDate)}`
                                : "—"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Badge className="border-sky-200 bg-white px-1.5 py-0 text-[8px] text-sky-700">
                                {tour ? TOUR_CFG[tour.tourType].label : "—"}
                              </Badge>
                            </span>
                            <span className="flex items-center justify-end gap-1">
                              <Badge
                                className={`justify-center px-1.5 py-0 text-[8px] ${requestStatus === "completed" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : requestStatus === "partial" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}
                              >
                                {requestStatus === "pending"
                                  ? "Unassigned"
                                  : requestStatus[0].toUpperCase() +
                                    requestStatus.slice(1)}
                              </Badge>
                              {active && (
                                <CheckCircle2 className="h-3.5 w-3.5 text-sky-600" />
                              )}
                            </span>
                          </button>
                        );
                      })}
                      {!searchedRequestTourIds.length && (
                        <div className="px-3 py-6 text-center text-xs text-slate-400">
                          No unassigned tours match this search.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ROOM REQUEST CARDS */}
            {selectedRoomRequest && selectedRequestTour && (
              <div className="border-t border-sky-200 bg-slate-50/60 p-2">
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                  {selectedTourRequests
                    .flatMap((request) =>
                      Array.from(
                        { length: Math.max(1, request.roomsRequested) },
                        (_, roomIndex) => ({ request, roomIndex }),
                      ),
                    )
                    .map(({ request, roomIndex }, slotIndex, requestSlots) => {
                      const applicants = getApplicantsForRequestRoom(
                        request,
                        selectedRequestTour,
                        roomIndex,
                      );
                      const headerRequest = selectedRoomRequest.venuePublicId === request.venuePublicId
                        ? selectedRoomRequest
                        : request;
                      const headerRoomIndex = headerRequest === selectedRoomRequest
                        ? selectedRequestRoomIndex
                        : roomIndex;
                      const headerAssignedRoomId = getAssignedRoomIdForRequestSlot(headerRequest, headerRoomIndex);
                      const headerProperty = properties.find((property) =>
                        property.rooms.some((room) => room.id === headerAssignedRoomId),
                      ) ?? properties.find((property) => property.id === headerRequest.propertyId);
                      const requestedRoomTone = roomTypeTone(request.roomType);
                      const active =
                        selectedRoomRequestId === request.id &&
                        selectedRequestRoomIndex === roomIndex;
                      const requestCompleted =
                        request.status?.toLowerCase().startsWith("complet") ===
                        true;
                      const slotKey = requestSlotKey(request.id, roomIndex);
                      const requestAssigned =
                        !locallyUnlinkedRequestSlots.has(slotKey) &&
                        (requestCompleted ||
                          Boolean(
                            getAssignedRoomIdForRequestSlot(request, roomIndex),
                          ));
                      const assignedRoomId = getAssignedRoomIdForRequestSlot(
                        request,
                        roomIndex,
                      );
                      const assignedRoom = properties
                        .flatMap((property) => property.rooms)
                        .find((room) => room.id === assignedRoomId);
                      // const applicantCategories = Array.from(
                      //   new Set(
                      //     applicants
                      //       .map((applicant) => applicant.category)
                      //       .filter((category): category is string => Boolean(category)),
                      //   ),
                      // );
                      const venueName =
                        request.venue
                          .toLowerCase()
                          .replace("kerbala", "karbala") === "karbala"
                          ? "Karbala"
                          : request.venue;
                      const previousVenueName =
                        slotIndex > 0
                          ? requestSlots[slotIndex - 1].request.venue
                              .toLowerCase()
                              .replace("kerbala", "karbala")
                          : "";
                      const startsVenueSection =
                        slotIndex === 0 ||
                        venueName.toLowerCase() !== previousVenueName;
                      const venueRoomNumber = requestSlots
                        .slice(0, slotIndex + 1)
                        .filter(
                          (slot) =>
                            slot.request.venue
                              .toLowerCase()
                              .replace("kerbala", "karbala") ===
                            request.venue
                              .toLowerCase()
                              .replace("kerbala", "karbala"),
                        ).length;
                      const unassignedApplicantIds = requestAssigned
                        ? []
                        : applicants.map((applicant) => applicant.id);
                      const selectedDragApplicantIds = unassignedApplicantIds;
                      return (
                        <div
                          key={`${request.id}-${roomIndex}`}
                          className="contents"
                        >
                          {startsVenueSection && (
                            <div className="mt-2 col-span-full flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2">
                              <MapPin className="h-3.5 w-3.5 text-emerald-700" />
                              <span className="text-xs font-bold text-slate-900">
                                {venueName} Room Requests
                              </span>
                              <span className="text-[10px] font-bold text-slate-700">
                                {fmt(request.checkInDate)} →{" "}
                                {fmt(request.checkOutDate)}
                              </span>
                              <span className="text-slate-300">•</span>
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500">
                                <Building2 className="h-3 w-3" />
                                {headerProperty?.name || venueName}
                              </span>
                              {selectedRequestTour.specialAccommodationRequest && (
                                <span
                                  className="max-w-[420px] truncate text-xs font-medium text-violet-700"
                                  title={
                                    selectedRequestTour.specialAccommodationRequest
                                  }
                                >
                                  <span className="font-semibold">
                                    Special request:
                                  </span>{" "}
                                  {
                                    selectedRequestTour.specialAccommodationRequest
                                  }
                                </span>
                              )}
                            </div>
                          )}
                          <div
                            role="button"
                            tabIndex={0}
                            draggable={!requestAssigned}
                            onDragStart={(event) => {
                              if (
                                event.target !== event.currentTarget ||
                                requestAssigned
                              )
                                return;
                              event.dataTransfer.effectAllowed = "move";
                              event.dataTransfer.setData(
                                "application/x-fhims-allocation",
                                JSON.stringify({
                                  kind: "group",
                                  tourId: request.tourId,
                                  applicantIds: selectedDragApplicantIds,
                                  requestId: request.id,
                                  roomIndex,
                                } satisfies DragAssignment),
                              );
                              setDraggingRequestSlotKey(slotKey);
                              setRoomRequestDragPreview(
                                event,
                                `Room ${venueRoomNumber}`,
                                request.roomType,
                                applicants.length,
                              );
                            }}
                            onDragEnd={() => setDraggingRequestSlotKey("")}
                            onClick={() =>
                              toggleRequestSlot(request, roomIndex)
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ")
                                event.currentTarget.click();
                            }}
                            className={`group/request relative rounded-lg border-2 bg-white ${requestAssigned ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} ${active ? "border-sky-500 shadow-sm ring-1 ring-sky-200" : requestAssigned ? "border-emerald-400 bg-emerald-50/35 hover:border-emerald-500" : "border-amber-400 bg-amber-50/35 hover:border-amber-500"}`}
                            title={
                              requestAssigned
                                ? "This room request is assigned"
                                : "Open or drag this request to a compatible room"
                            }
                          >
                            <div className="px-2.5 py-1.5 text-left">
                              <div className="flex min-h-5 min-w-0 items-center gap-1.5">
                                <span className="truncate text-[11px] font-semibold text-slate-800">
                                  Room {venueRoomNumber}
                                </span>
                                <Badge
                                  className={`shrink-0 px-1.5 py-0 text-[8px] ${requestedRoomTone.border} ${requestedRoomTone.bg} ${requestedRoomTone.text}`}
                                >
                                  {request.roomType}
                                </Badge>
                                {requestAssigned &&
                                  (assignedRoom ||
                                    request.assignedRoomNumber) && (
                                    <Badge className="shrink-0 gap-1 border-emerald-200 bg-white py-0 pl-1.5 pr-0.5 text-[8px] text-emerald-700">
                                      <span>
                                        {assignedRoom?.number ||
                                          request.assignedRoomNumber}
                                      </span>
                                      <button
                                        type="button"
                                        draggable={false}
                                        aria-label={`Remove room assignment ${assignedRoom?.number || request.assignedRoomNumber}`}
                                        title="Remove room assignment"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setRoomUnallocationTarget({
                                            request,
                                            roomIndex,
                                            assignedRoomNumber:
                                              assignedRoom?.number ||
                                              request.assignedRoomNumber,
                                          });
                                        }}
                                        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                                      >
                                        <X className="h-2.5 w-2.5" />
                                      </button>
                                    </Badge>
                                  )}
                                <span className="shrink-0 text-[8px] font-medium text-slate-500">
                                  {applicants.length} applicant
                                  {applicants.length === 1 ? "" : "s"}
                                </span>
                                {/* {applicantCategories.length > 0 && <span className="max-w-28 truncate rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[7px] font-medium text-slate-600" title={applicantCategories.join(", ")}>{applicantCategories.join(", ")}</span>} */}
                                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                                  {requestAssigned && (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                  )}
                                </div>
                              </div>
                            </div>
                            <div
                              className={`pointer-events-none invisible absolute bottom-full left-0 z-[80] mb-2 w-[520px] max-w-[calc(100vw-3rem)] translate-y-1 rounded-xl border border-slate-200 bg-white p-3 opacity-0 shadow-2xl transition-all group-hover/request:visible group-hover/request:translate-y-0 group-hover/request:opacity-100 ${draggingRequestSlotKey === slotKey ? "!hidden" : ""}`}
                            >
                              <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
                                <div className="text-[10px] font-semibold text-slate-800">
                                  Applicants in Room {venueRoomNumber}
                                </div>
                                <div className="text-[9px] text-slate-500">
                                  {applicants.length} applicant
                                  {applicants.length === 1 ? "" : "s"}
                                </div>
                              </div>
                              <div className="grid max-h-56 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
                                {applicants.length === 0 && (
                                  <div className="col-span-full py-4 text-center text-[10px] text-slate-500">
                                    No applicants
                                  </div>
                                )}
                                {applicants.map((applicant) => (
                                  <div
                                    key={applicant.id}
                                    className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/70 p-2 text-left"
                                  >
                                    {applicant.photo ? (
                                      <img
                                        src={applicant.photo}
                                        alt={applicant.name}
                                        className="h-8 w-8 shrink-0 rounded-full border border-white object-cover shadow-sm"
                                      />
                                    ) : (
                                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-slate-500 shadow-sm">
                                        <User className="h-4 w-4" />
                                      </span>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-[10px] font-semibold text-slate-900">
                                        {applicant.name}
                                      </div>
                                      <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[8px] text-slate-500">
                                        <span>
                                          ITS {applicant.itsId || "N/A"}
                                        </span>
                                        <span>
                                          Vatan: {applicant.vatan || "N/A"}
                                        </span>
                                        <span>
                                          Age: {applicant.age || "N/A"}
                                        </span>
                                        <span>
                                          Category:{" "}
                                          {applicant.category || "N/A"}
                                        </span>
                                      </div>
                                    </div>
                                    <span
                                      className={`shrink-0 rounded border px-1.5 py-0.5 text-[7px] font-semibold ${passengerTone(applicant.age)}`}
                                    >
                                      {applicant.applicantType || "N/A"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-white p-2.5">
                  <div className="min-w-48 flex-1">
                    <div className="text-[10px] font-semibold text-slate-800">
                      Manual room allotment
                    </div>
                    <div className="text-[8px] text-slate-500">
                      Select one compatible room and assign the complete request
                      of {selectedRequestApplicantIds.size} applicant
                      {selectedRequestApplicantIds.size === 1 ? "" : "s"} in
                      this request.
                    </div>
                  </div>
                  <Select
                    value={selectedAllocationRoomId}
                    onValueChange={setSelectedAllocationRoomId}
                    disabled={roomAssignmentSaving}
                  >
                    <SelectTrigger className="h-8 w-full border-sky-200 bg-white text-xs sm:w-80">
                      <SelectValue placeholder="Select property & room" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableRequestRooms.map(({ property, room }) => (
                        <SelectItem
                          key={`${property.id}-${room.id}`}
                          value={room.id}
                        >
                          {property.name} · {room.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    onClick={assignSelectedApplicants}
                    aria-busy={roomAssignmentSaving}
                    disabled={roomAssignmentSaving || !selectedAllocationTarget}
                    className="h-8 bg-sky-600 px-3 text-xs text-white hover:bg-sky-700"
                  >
                    {roomAssignmentSaving ? "Assigning..." : "Assign room"}
                  </Button>
                  <Button
                    type="button"
                    onClick={openAutoAllocationConfirmation}
                    aria-busy={
                      autoAllocationTarget !== null && roomAssignmentSaving
                    }
                    disabled={roomAssignmentSaving || !selectedRequestTour?.id}
                    className="h-8 bg-sky-600 px-3 text-xs text-white hover:bg-sky-700"
                  >
                    {autoAllocationTarget !== null && roomAssignmentSaving
                      ? "Auto-allocating..."
                      : "Auto-allocate room"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Main 3+1 Layout ── */}
        <div
          id="room-inventory"
          className="scroll-mt-4 grid grid-cols-12 gap-4 items-start"
        >
          {/* ── Full-width matching rooms ── */}
          <div className="col-span-12 space-y-2">
            {visibleProperties.length === 0 && (
              <Card className="border-sky-200/50 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-10 text-center text-sky-400 text-sm">
                  No properties match the selected filters.
                </CardContent>
              </Card>
            )}
            {visibleProperties.map((property) => {
              const rooms = getRooms(property);
              const ttc = TOUR_CFG[property.tourType];
              const isNajafProperty =
                property.location
                  .toLowerCase()
                  .replace("kerbala", "karbala") === "najaf";
              return (
                <Card
                  key={property.id}
                  className={`${isNajafProperty ? "border-emerald-200 bg-emerald-50/45" : "border-violet-200 bg-violet-50/45"} backdrop-blur-sm shadow-md`}
                >
                  <CardContent className="p-3">
                    <div className="mb-2 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-base shadow-sm">
                          {property.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-base text-sky-900">
                              {property.name}
                            </h3>
                            <Badge
                              className={`${ttc.bg} ${ttc.text} ${ttc.border} text-[9px] px-1.5 py-0 flex items-center gap-0.5`}
                            >
                              {ttc.icon} {ttc.label}
                            </Badge>
                          </div>
                          <p className="text-xs text-sky-600 flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {property.location} · {property.totalRooms} rooms
                          </p>
                        </div>
                      </div>
                    </div>

                    {rooms.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-sky-200 bg-sky-50/50 px-4 py-8 text-center text-xs text-slate-500">
                        No room occupancy or availability matches the selected
                        date window. Property remains available in the list.
                      </div>
                    ) : (
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,112px))] gap-2">
                        {rooms.map((room, roomIndex) => {
                          const allocatedSlots =
                            allocatedRequestSlotsByRoomId.get(room.id) ?? [];
                          const allocatedRequestSlot =
                            allocatedSlots.find(
                              ({ request }) =>
                                request.tourId === selectedInventoryTourId,
                            ) ?? allocatedSlots[0];
                          const assignedApplicants =
                            getAssignedApplicantsForRoom(room);
                          const displayedRoom: Room =
                            assignedApplicants.length === room.occupied &&
                            room.occupants?.length === assignedApplicants.length
                              ? room
                              : {
                                  ...room,
                                  occupants: assignedApplicants,
                                  occupied: assignedApplicants.length,
                                  status: allocatedRequestSlot
                                    ? assignedApplicants.length >= room.capacity
                                      ? "occupied"
                                      : "partial"
                                    : room.status === "maintenance" ||
                                        room.status === "blocked"
                                      ? room.status
                                      : assignedApplicants.length === 0
                                        ? "available"
                                        : assignedApplicants.length >=
                                            room.capacity
                                          ? "occupied"
                                          : "partial",
                                };
                          return (
                            <div key={room.id}>
                              <RoomCard
                                room={displayedRoom}
                                propertyId={property.id}
                                onDropAssignment={dropAssignmentOnRoom}
                                allocated={Boolean(allocatedRequestSlot)}
                                selected={selectedAllocationRoomId === room.id}
                                tooltipAlign={
                                  roomIndex === 0
                                    ? "left"
                                    : roomIndex === rooms.length - 1
                                      ? "right"
                                      : "center"
                                }
                                onSelect={() => {
                                  const canSelect = selectableRequestRooms.some(
                                    ({ room: selectableRoom }) =>
                                      selectableRoom.id === room.id,
                                  );
                                  if (canSelect)
                                    setSelectedAllocationRoomId(room.id);
                                }}
                                onReallocateRoom={(
                                  propertyId,
                                  selectedRoom,
                                ) => {
                                  if (!allocatedRequestSlot) return;
                                  setRoomReallocationTarget({
                                    propertyId,
                                    room: selectedRoom,
                                    request: allocatedRequestSlot.request,
                                    roomIndex: allocatedRequestSlot.roomIndex,
                                  });
                                  setRoomTargetPropertyId("");
                                  setRoomTargetType("");
                                  setRoomTargetRoomId("");
                                  setRoomReallocationReason("");
                                  setRoomReallocationPeriod("all");
                                  setRoomReallocationFrom("");
                                  setRoomReallocationTo("");
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* ── RIGHT: Families & Applicants panel (col 10-12) ── */}
          <div className="hidden">
            <div className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-sky-100 bg-sky-50 px-3 py-2.5">
                <ClipboardList className="h-4 w-4 text-sky-700" />
                <div>
                  <div className="text-xs font-semibold text-slate-900">
                    Tour Room Requests
                  </div>
                  <div className="text-[9px] text-slate-500">
                    Select a tour to open its room requests
                  </div>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {requestTourIds.map((tourId) => {
                  const tour = tours.find((item) => item.id === tourId);
                  const requests = roomRequests.filter(
                    (request) => request.tourId === tourId,
                  );
                  const requestApplicantIds = new Set(
                    requests.flatMap((request) => request.applicantIds),
                  );
                  const applicants = tour
                    ? [
                        ...tour.clubbedFamilies.flatMap(
                          (family) => family.members,
                        ),
                        ...tour.nonClubbedApplicants,
                      ].filter((applicant) =>
                        requestApplicantIds.has(applicant.id),
                      )
                    : [];
                  const assigned = applicants.filter(
                    (applicant) => applicant.roomAssigned,
                  ).length;
                  const active = selectedRoomRequest?.tourId === tourId;
                  const status =
                    assigned === 0
                      ? "Pending"
                      : assigned >= applicants.length
                        ? "Allocated"
                        : "In Progress";
                  const statusClass =
                    status === "Allocated"
                      ? "bg-emerald-100 text-emerald-700"
                      : status === "In Progress"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-rose-100 text-rose-700";
                  return (
                    <button
                      key={tourId}
                      type="button"
                      onClick={() => {
                        const firstRequest = requests[0];
                        setSelectedRoomRequestId(firstRequest?.id ?? "");
                        setSelectedRequestRoomType(
                          firstRequest?.roomType ?? "",
                        );
                        setSelectedRequestPropertyId(
                          firstRequest?.propertyId ?? "",
                        );
                        setSelectedRequestPropertyRoomTypeId(
                          firstRequest?.propertyRoomTypeId,
                        );
                        setTourApplicantSearch("");
                        setSelectedRequestApplicantIds(new Set());
                        setSelectedAllocationRoomId("");
                      }}
                      className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${active ? "bg-sky-50" : "hover:bg-slate-50"}`}
                    >
                      <div
                        className={`h-2.5 w-2.5 shrink-0 rounded-full ${status === "Allocated" ? "bg-emerald-500" : status === "In Progress" ? "bg-amber-500" : "bg-rose-500"}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-semibold text-slate-800">
                          {tour?.name}
                        </div>
                        <div className="mt-0.5 text-[8px] text-slate-500">
                          {applicants.length} applicants · {requests.length}{" "}
                          room type{requests.length === 1 ? "" : "s"}
                        </div>
                      </div>
                      <Badge
                        className={`${statusClass} border-0 px-1.5 py-0 text-[8px]`}
                      >
                        {status}
                      </Badge>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  );
                })}
              </div>
              {selectedRoomRequest && selectedTourRequests.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-t border-sky-100 bg-slate-50 px-3 py-2">
                  <span className="mr-1 self-center text-[8px] font-semibold uppercase text-slate-400">
                    Room type
                  </span>
                  {selectedTourRequests.map((request) => (
                    <button
                      key={request.id}
                      type="button"
                      onClick={() => {
                        setSelectedRoomRequestId(request.id);
                        setSelectedRequestRoomType(request.roomType);
                        setSelectedRequestPropertyId(request.propertyId);
                        setSelectedRequestPropertyRoomTypeId(
                          request.propertyRoomTypeId,
                        );
                        setSelectedRequestApplicantIds(new Set());
                        setSelectedAllocationRoomId("");
                      }}
                      className={`rounded-full border px-2 py-1 text-[9px] font-semibold ${selectedRoomRequest.id === request.id ? "border-sky-500 bg-sky-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-sky-300"}`}
                    >
                      {standardRoomType(
                        Math.ceil(
                          request.applicantIds.length /
                            Math.max(1, request.roomsRequested),
                        ),
                      )}{" "}
                      · {request.roomsRequested}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative rounded-xl border border-sky-200 bg-white p-2 shadow-sm">
              <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={tourApplicantSearch}
                onChange={(event) => setTourApplicantSearch(event.target.value)}
                placeholder="Search tours, applicants or ITS..."
                className="h-8 border-0 bg-slate-50 pl-8 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-sky-300"
              />
            </div>
            {selectedRoomRequest ? (
              <div className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm">
                <div className="border-b border-sky-100 bg-gradient-to-r from-sky-50 to-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-900">
                        New Applicants to Allocate
                      </div>
                      <div className="mt-0.5 text-[9px] text-slate-500">
                        Request {selectedRoomRequest.id} · select applicants and
                        give a room number
                      </div>
                    </div>
                    <Badge className="border-amber-200 bg-amber-50 px-1.5 py-0 text-[9px] text-amber-700">
                      {unassignedRequestApplicants.length} unassigned
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge className="border-violet-200 bg-violet-50 text-[9px] text-violet-700">
                      {standardRoomType(selectedRequestApplicants.length)}
                    </Badge>
                    <Badge className="border-slate-200 bg-white text-[9px] text-slate-600">
                      {selectedRequestTour?.name}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2 border-b border-sky-100 bg-slate-50/80 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold text-sky-700">
                      Complete room request
                    </span>
                    <Badge className="border-sky-200 bg-white text-[9px] text-sky-700">
                      {selectedRequestApplicantIds.size} applicant
                      {selectedRequestApplicantIds.size === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={selectedAllocationRoomId}
                      onValueChange={setSelectedAllocationRoomId}
                      disabled={roomAssignmentSaving}
                    >
                      <SelectTrigger className="h-8 min-w-0 flex-1 border-sky-200 bg-white text-xs">
                        <SelectValue placeholder="Select property & room" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableRequestRooms.map(({ property, room }) => (
                          <SelectItem
                            key={`${property.id}-${room.id}`}
                            value={room.id}
                          >
                            {property.name} · {room.number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      onClick={assignSelectedApplicants}
                      aria-busy={roomAssignmentSaving}
                      disabled={
                        roomAssignmentSaving || !selectedAllocationTarget
                      }
                      className="h-8 shrink-0 bg-sky-600 px-3 text-xs text-white hover:bg-sky-700"
                    >
                      {roomAssignmentSaving ? "Assigning..." : "Assign room"}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 p-3">
                  {searchedRequestApplicants.length > 0 ? (
                    searchedRequestApplicants.map((applicant) => (
                      <div
                        key={applicant.id}
                        className="flex items-center gap-2 rounded-xl bg-sky-50/60 ring-1 ring-sky-100"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-1">
                            {selectedRequestTour?.clubbedFamilies.find(
                              (family) =>
                                family.members.some(
                                  (member) => member.id === applicant.id,
                                ),
                            ) && (
                              <Badge className="border-teal-200 bg-teal-50 px-1.5 py-0 text-[8px] text-teal-700">
                                Clubbed family ·{" "}
                                {
                                  selectedRequestTour.clubbedFamilies.find(
                                    (family) =>
                                      family.members.some(
                                        (member) => member.id === applicant.id,
                                      ),
                                  )?.familyCode
                                }
                              </Badge>
                            )}
                            {applicant.age < 2 && (
                              <Badge className="border-violet-200 bg-violet-50 px-1.5 py-0 text-[8px] text-violet-700">
                                Infant
                              </Badge>
                            )}
                            {applicant.age >= 2 && applicant.age < 12 && (
                              <Badge className="border-amber-200 bg-amber-50 px-1.5 py-0 text-[8px] text-amber-700">
                                Child
                              </Badge>
                            )}
                          </div>
                          <ApplicantSummaryCard
                            applicant={applicant}
                            tourId={selectedRoomRequest.tourId}
                            onReallocate={openReallocation}
                            onDeallocate={openDeallocation}
                          />
                        </div>
                      </div>
                    ))
                  ) : unassignedRequestApplicants.length ? (
                    <div className="rounded-lg border border-dashed border-slate-200 px-3 py-8 text-center text-xs text-slate-500">
                      No new applicants match your search.
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/50 px-3 py-8 text-center">
                      <CheckCircle2 className="mx-auto mb-2 h-7 w-7 text-emerald-500" />
                      <div className="text-xs font-semibold text-emerald-800">
                        All applicants allotted
                      </div>
                      <div className="mt-1 text-[10px] text-slate-500">
                        Applicants assigned from this request are now shown on
                        the left.
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t border-sky-100 bg-sky-50/60 px-3 py-2 text-[9px] text-sky-700">
                  Select applicants and choose “Property · Room”, or drag an
                  applicant directly onto a room number.
                </div>
              </div>
            ) : (
              <>
                {rightPanelTours.length === 0 && (
                  <div className="rounded-xl border border-dashed border-sky-200 bg-white p-6 text-center text-xs text-slate-500">
                    No tours or applicants match the selected filters.
                  </div>
                )}
                {rightPanelTours.map((tour) => {
                  const ttc = TOUR_CFG[tour.tourType];
                  const isManualUnassigned =
                    tour.id === MANUAL_UNASSIGNED_TOUR_ID;
                  const isCollapsed = collapsedTours.has(tour.id);
                  const tourNameMatches =
                    !tourApplicantQuery ||
                    tour.name.toLowerCase().includes(tourApplicantQuery);
                  const visibleFamilies = tourNameMatches
                    ? tour.clubbedFamilies
                    : tour.clubbedFamilies
                        .map((family) => {
                          const familyMatches =
                            family.hofName
                              .toLowerCase()
                              .includes(tourApplicantQuery) ||
                            family.hofItsId.includes(tourApplicantQuery) ||
                            family.familyCode
                              .toLowerCase()
                              .includes(tourApplicantQuery);
                          return familyMatches
                            ? family
                            : {
                                ...family,
                                members: family.members.filter(
                                  (member) =>
                                    member.name
                                      .toLowerCase()
                                      .includes(tourApplicantQuery) ||
                                    member.itsId.includes(tourApplicantQuery),
                                ),
                              };
                        })
                        .filter((family) => family.members.length > 0);
                  const visibleIndividuals = tourNameMatches
                    ? tour.nonClubbedApplicants
                    : tour.nonClubbedApplicants.filter(
                        (applicant) =>
                          applicant.name
                            .toLowerCase()
                            .includes(tourApplicantQuery) ||
                          applicant.itsId.includes(tourApplicantQuery),
                      );
                  const totalApplicants =
                    visibleFamilies.reduce(
                      (count, family) => count + family.members.length,
                      0,
                    ) + visibleIndividuals.length;
                  return (
                    <div
                      key={tour.id}
                      className="rounded-xl border border-slate-200 bg-white shadow-sm"
                    >
                      <button
                        type="button"
                        className="flex w-full items-start gap-2.5 p-3 text-left transition-colors hover:bg-slate-50"
                        onClick={() => toggleTourCollapse(tour.id)}
                      >
                        <div
                          className={`flex-shrink-0 rounded-lg p-2 ${isManualUnassigned ? "bg-amber-100" : ttc.bg}`}
                        >
                          <span
                            className={
                              isManualUnassigned ? "text-amber-700" : ttc.text
                            }
                          >
                            {isManualUnassigned ? (
                              <UserPlus className="h-4 w-4" />
                            ) : (
                              ttc.icon
                            )}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-semibold text-slate-900">
                            {tour.name}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            {!isManualUnassigned && (
                              <Badge
                                className={`${ttc.bg} ${ttc.text} ${ttc.border} px-1.5 py-0 text-[8px]`}
                              >
                                {ttc.label}
                              </Badge>
                            )}
                            <span className="text-[9px] text-slate-500">
                              {totalApplicants} applicants
                            </span>
                            {!isManualUnassigned && (
                              <>
                                <span className="text-[9px] text-slate-300">
                                  •
                                </span>
                                <span className="text-[9px] text-slate-500">
                                  {fmt(tour.startDate)} – {fmt(tour.endDate)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <span className="mt-0.5 flex-shrink-0 text-slate-500">
                          {isCollapsed ? (
                            <ChevronRight className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </span>
                      </button>

                      {!isCollapsed && (
                        <div className="space-y-3 border-t border-slate-100 p-3">
                          {visibleFamilies.length > 0 && (
                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  <Users className="h-3.5 w-3.5 text-sky-600" />{" "}
                                  Family Groups
                                </div>
                                <span className="text-[9px] text-slate-400">
                                  Hover a group for all applicants
                                </span>
                              </div>
                              <div className="space-y-2">
                                {visibleFamilies.map((family, idx) => (
                                  <FamilySummaryCard
                                    key={family.id}
                                    family={family}
                                    index={idx}
                                    tourId={tour.id}
                                    onReallocate={openReallocation}
                                    onDeallocate={openDeallocation}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {visibleIndividuals.length > 0 && (
                            <div className="border-t border-slate-100 pt-3">
                              <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  <User className="h-3.5 w-3.5 text-amber-600" />{" "}
                                  {isManualUnassigned
                                    ? "Manually Added Applicants"
                                    : "Individual Applicants"}
                                </div>
                                <Badge className="border-amber-200 bg-amber-50 px-1.5 py-0 text-[9px] text-amber-700">
                                  {visibleIndividuals.length}
                                </Badge>
                              </div>
                              <div className="space-y-2">
                                {visibleIndividuals.map((applicant) => (
                                  <ApplicantSummaryCard
                                    key={applicant.id}
                                    applicant={applicant}
                                    tourId={tour.id}
                                    onReallocate={openReallocation}
                                    onDeallocate={openDeallocation}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
      {/* MANAGE APPROVED APPLICANTS */}

      {/* ADD APPROVED APPLICANTS / NOT IN USE*/}
      <Dialog
        open={applicantDialogOpen}
        onOpenChange={(open) => {
          setApplicantDialogOpen(open);
          if (!open) setSelectedApprovedApplicantIds(new Set());
        }}
      >
        <DialogContent className="max-h-[88vh] overflow-hidden bg-white sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-900">
              <UserPlus className="h-5 w-5" /> Add Applicants
            </DialogTitle>
            <DialogDescription>
              Applicants who have not yet been added for room allocation are
              shown with their submitting Tour Operator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 overflow-hidden py-1">
            <div className="flex items-center gap-2 rounded-xl border border-sky-100 bg-sky-50/70 p-2.5">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={approvedApplicantSearch}
                  onChange={(event) =>
                    setApprovedApplicantSearch(event.target.value)
                  }
                  placeholder="Search applicant, ITS or Tour Operator..."
                  className="h-9 border-sky-200 bg-white pl-8 text-xs"
                />
              </div>
              <Badge className="shrink-0 border-sky-200 bg-white text-sky-700">
                {remainingApprovedApplicants.length} remaining
              </Badge>
            </div>

            <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-slate-200">
              {filteredApprovedApplicants.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {filteredApprovedApplicants.map((applicant) => {
                    const selected = selectedApprovedApplicantIds.has(
                      applicant.id,
                    );
                    return (
                      <label
                        key={applicant.id}
                        className={`grid cursor-pointer grid-cols-[auto_1.5fr_1fr] items-center gap-3 p-3 transition-colors ${selected ? "bg-sky-50" : "bg-white hover:bg-slate-50"}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleApprovedApplicant(applicant.id)}
                          className="h-4 w-4 accent-sky-600"
                        />
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base ${applicant.gender === "Female" ? "bg-rose-100" : "bg-sky-100"}`}
                          >
                            {applicant.gender === "Female" ? "👩" : "👨"}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-xs font-semibold text-slate-900">
                              {applicant.name}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              ITS {applicant.itsId} · {applicant.gender} ·{" "}
                              {applicant.age}y
                            </div>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[9px] font-semibold uppercase tracking-wide text-sky-500">
                            Tour Operator
                          </div>
                          <div className="truncate text-[11px] font-semibold text-sky-800">
                            {applicant.tourOperator}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                  <CheckCircle2 className="mb-2 h-9 w-9 text-emerald-400" />
                  <div className="text-sm font-semibold text-slate-700">
                    No applicants remaining
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    All available applicants have been added, or no records
                    match your search.
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="items-center sm:justify-between">
            <div className="mr-auto text-xs text-slate-500">
              {selectedApprovedApplicantIds.size} applicant
              {selectedApprovedApplicantIds.size === 1 ? "" : "s"} selected
            </div>
            <Button
              variant="outline"
              onClick={() => setApplicantDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={addApprovedApplicants}
              disabled={!selectedApprovedApplicantIds.size}
              className="bg-sky-600 text-white hover:bg-sky-700"
            >
              <UserPlus className="mr-1.5 h-4 w-4" />
              Add Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* NOT IN USE */}

      {/* REALLOCATE EVERY APPLICANT IN A ROOM */}
      <Dialog
        open={!!roomReallocationTarget}
        onOpenChange={(open) => {
          if (!open && !roomAssignmentSaving) {
            setRoomReallocationTarget(null);
          }
        }}
      >
        <DialogContent className="!flex max-h-[calc(100dvh-1rem)] !w-[calc(100vw-1rem)] !max-w-[1280px] flex-col !gap-0 overflow-hidden rounded-xl bg-white !p-0 sm:max-h-[92dvh] sm:!w-[94vw] lg:!w-[92vw]">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-4 py-4 pr-12 text-left sm:px-6 sm:pr-14 lg:px-8 lg:py-5 lg:pr-16">
            <DialogTitle className="flex items-start gap-2.5 text-lg leading-tight text-slate-900 sm:items-center sm:text-xl">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700 sm:h-10 sm:w-10">
                <Repeat2 className="h-4 w-4" />
              </span>
              <span className="min-w-0">Reallocate Complete Room</span>
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-slate-500 sm:pl-[50px]">
              All applicants currently assigned to this room will move together.
              Select the exact destination and record the reason.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 lg:px-8 lg:py-5">
            <div className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-xs sm:grid-cols-2 xl:grid-cols-4">
              <div className="min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  TO Name
                </div>
                <div className="mt-0.5 break-words font-semibold text-slate-900">
                  {roomReallocationRequest?.requestedBy || "—"}
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Tour Name
                </div>
                <div className="mt-0.5 break-words font-semibold text-slate-900">
                  {roomReallocationTour?.name || "—"}
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Tour Code
                </div>
                <div className="mt-0.5 break-all font-mono text-[11px] font-semibold leading-4 text-slate-900">
                  {roomReallocationTour?.tourCode || "—"}
                </div>
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500">
                      Current allocation
                    </div>
                    <div className="break-words text-sm font-semibold text-slate-900">
                      {roomReallocationProperty?.name} · Room{" "}
                      {roomReallocationTarget?.room.number}
                    </div>
                  </div>
                  <Badge className="shrink-0 border-slate-200 bg-slate-50 text-slate-700">
                    {roomReallocationOccupants.length} applicant
                    {roomReallocationOccupants.length === 1 ? "" : "s"}
                  </Badge>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {roomReallocationOccupants.map((applicant) => (
                    <span
                      key={applicant.id}
                      className="max-w-full break-words rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-700"
                    >
                      {applicant.gender === "Female" ? "👩" : "👨"}{" "}
                      {applicant.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:flex-col lg:items-stretch xl:flex-row xl:items-center">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-900">
                      Requested stay
                    </div>
                    <div className="break-words text-[11px] text-slate-500">
                      {roomReallocationRequest?.checkInDate || "—"} to{" "}
                      {roomReallocationRequest?.checkOutDate || "—"}
                    </div>
                  </div>
                  <div
                    role="group"
                    aria-label="Reallocation period"
                    className="grid w-full shrink-0 grid-cols-2 rounded-md border border-slate-200 bg-slate-50 p-1 sm:w-auto lg:w-full xl:w-auto"
                  >
                    <button
                      type="button"
                      aria-pressed={roomReallocationPeriod === "all"}
                      onClick={() => setRoomReallocationPeriod("all")}
                      className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1 ${roomReallocationPeriod === "all" ? "bg-sky-600 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}
                    >
                      All days
                    </button>
                    <button
                      type="button"
                      aria-pressed={roomReallocationPeriod === "partial"}
                      onClick={() => {
                        setRoomReallocationPeriod("partial");
                        setRoomReallocationFrom(
                          roomReallocationRequest?.checkInDate || "",
                        );
                        setRoomReallocationTo(
                          roomReallocationRequest?.checkOutDate || "",
                        );
                      }}
                      className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1 ${roomReallocationPeriod === "partial" ? "bg-sky-600 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}
                    >
                      Partial days
                    </button>
                  </div>
                </div>
                {roomReallocationPeriod === "partial" && (
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                    <div className="min-w-0">
                      <div className="mb-1 text-[10px] font-medium text-slate-600">
                        Change room from
                      </div>
                      <Input
                        type="date"
                        aria-label="Change room from"
                        className="min-w-0 text-sm [color-scheme:light]"
                        min={roomReallocationRequest?.checkInDate}
                        max={roomReallocationRequest?.checkOutDate}
                        value={roomReallocationFrom}
                        onChange={(event) =>
                          setRoomReallocationFrom(event.target.value)
                        }
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="mb-1 text-[10px] font-medium text-slate-600">
                        Change room until
                      </div>
                      <Input
                        type="date"
                        aria-label="Change room until"
                        className="min-w-0 text-sm [color-scheme:light]"
                        min={
                          roomReallocationFrom ||
                          roomReallocationRequest?.checkInDate
                        }
                        max={roomReallocationRequest?.checkOutDate}
                        value={roomReallocationTo}
                        onChange={(event) =>
                          setRoomReallocationTo(event.target.value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                New allocation
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="min-w-0 md:col-span-2 lg:col-span-1">
                  <div className="mb-1 text-xs font-medium text-slate-700">
                    Property Type <span className="text-red-500">*</span>
                  </div>
                  <Select
                    value={roomTargetPropertyTypeId}
                    onValueChange={(value) => {
                      setRoomTargetPropertyTypeId(value);
                      setRoomTargetPropertyId("");
                      setRoomTargetProperties([]);
                      setRoomTargetType("");
                      setRoomTargetRoomId("");
                      setRoomTargetRoomTypes([]);
                      setRoomTargetRooms([]);
                      setRoomTargetRoomTypesLoading(false);
                      setRoomTargetRoomsLoading(false);
                    }}
                    disabled={roomAssignmentSaving || roomPropertyTypesLoading || !roomPropertyTypes.length}
                  >
                    <SelectTrigger
                      aria-label="Property Type"
                      className="min-w-0 overflow-hidden [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate"
                    >
                      <SelectValue placeholder={roomPropertyTypesLoading
                        ? "Loading property types..."
                        : roomPropertyTypesError
                          ? "Unable to load property types"
                          : roomPropertyTypes.length ? "Select property type" : "No property types available"} />
                    </SelectTrigger>

                    <SelectContent>
                      {roomPropertyTypes.map((propertyType) => (
                        <SelectItem key={propertyType.publicId} value={propertyType.publicId}>
                          {propertyType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-0 md:col-span-2 lg:col-span-1">
                  <div className="mb-1 text-xs font-medium text-slate-700">
                    New Property <span className="text-red-500">*</span>
                  </div>
                  <Select
                    value={roomTargetPropertyId}
                    onValueChange={(value) => {
                      setRoomTargetPropertyId(value);
                      setRoomTargetType("");
                      setRoomTargetRoomId("");
                      setRoomTargetRoomTypes([]);
                      setRoomTargetRoomTypesLoading(true);
                      setRoomTargetRooms([]);
                      setRoomTargetRoomsLoading(false);
                      setRoomReallocationReason("");
                    }}
                    disabled={
                      roomAssignmentSaving ||
                      !roomTargetPropertyTypeId ||
                      roomTargetPropertiesLoading ||
                      !roomTargetProperties.length
                    }
                  >
                    <SelectTrigger
                      aria-label="New property"
                      className="min-w-0 overflow-hidden [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate"
                    >
                      <SelectValue
                        placeholder={
                          !roomTargetPropertyTypeId
                            ? "Select property type first"
                            : roomTargetPropertiesLoading
                            ? "Loading properties..."
                            : roomTargetProperties.length
                              ? "Select property"
                              : "No available properties"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)] border border-slate-200">
                      {roomTargetProperties.map((property) => (
                        <SelectItem
                          key={property.propertyPublicId}
                          value={property.propertyPublicId}
                        >
                          {property.propertyName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-0">
                  <div className="mb-1 text-xs font-medium text-slate-700">
                    New Room Type <span className="text-red-500">*</span>
                  </div>
                  <Select
                    value={roomTargetType}
                    onValueChange={(value) => {
                      setRoomTargetType(value);
                      setRoomTargetRoomId("");
                      setRoomTargetRooms([]);
                      setRoomTargetRoomsLoading(true);
                      setRoomReallocationReason("");
                    }}
                    disabled={
                      !roomTargetPropertyId ||
                      roomTargetRoomTypesLoading ||
                      !roomTargetRoomTypes.length ||
                      roomAssignmentSaving
                    }
                  >
                    <SelectTrigger
                      aria-label="New room type"
                      className="min-w-0 overflow-hidden [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate"
                    >
                      <SelectValue
                        placeholder={
                          roomTargetRoomTypesLoading
                            ? "Loading room types..."
                            : roomTargetRoomTypes.length
                              ? "Select room type"
                              : roomTargetPropertyId
                                ? "No room types available"
                                : "Select property first"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)] border border-slate-200">
                      {roomTargetRoomTypes.map((roomType) => (
                        <SelectItem
                          key={roomType.publicId}
                          value={roomType.publicId}
                        >
                          {roomType.roomName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-0">
                  <div className="mb-1 text-xs font-medium text-slate-700">
                    New Room No. <span className="text-red-500">*</span>
                  </div>
                  <Select
                    value={roomTargetRoomId}
                    onValueChange={selectRoomReallocationDestination}
                    disabled={
                      !roomTargetType ||
                      roomTargetRoomsLoading ||
                      !roomTargetRooms.length ||
                      roomAssignmentSaving
                    }
                  >
                    <SelectTrigger
                      aria-label="New room number"
                      className="min-w-0 overflow-hidden [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate"
                    >
                      <SelectValue
                        placeholder={
                          roomTargetRoomsLoading
                            ? "Loading rooms..."
                            : roomTargetType && !roomTargetRooms.length
                              ? "No rooms available"
                              : "Select room no."
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)] border border-slate-200">
                      {roomTargetRooms.map((room) => (
                        <SelectItem
                          key={availableRoomOptionValue(room)}
                          value={availableRoomOptionValue(room)}
                        >
                          #{room.roomNumber.replace(/^#/, "")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            {roomTargetType &&
              !roomTargetRoomsLoading &&
              !roomTargetRooms.length && (
                <div
                  role="status"
                  aria-live="polite"
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                >
                  No rooms are available for the selected property, room type,
                  and stay dates.
                </div>
              )}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <label
                htmlFor="room-reallocation-reason"
                className="mb-1 block text-xs font-medium text-slate-700"
              >
                Reason for Reallocation <span className="text-red-500">*</span>
              </label>
              <TextareaUI
                id="room-reallocation-reason"
                aria-required="true"
                value={roomReallocationReason}
                onChange={(event) =>
                  setRoomReallocationReason(event.target.value)
                }
                disabled={roomAssignmentSaving}
                placeholder="Enter the specific reason for moving the complete room allocation..."
                className="min-h-24 !resize-y rounded-lg !border !border-solid !border-slate-300 !bg-white !p-3 text-sm focus:!border-sky-500 focus:!ring-2 focus:!ring-sky-100"
              />
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t border-slate-200 bg-slate-50/80 px-4 py-3 shadow-[0_-8px_24px_-22px_rgba(15,23,42,0.45)] sm:px-6 lg:px-8">
            <Button
              variant="outline"
              onClick={() => setRoomReallocationTarget(null)}
              disabled={roomAssignmentSaving}
              className="w-full sm:w-auto"
            >
              Keep Current Room
            </Button>
            <Button
              onClick={saveRoomReallocation}
              aria-busy={roomAssignmentSaving}
              disabled={
                roomAssignmentSaving ||
                roomTargetPropertiesLoading ||
                roomTargetRoomTypesLoading ||
                roomTargetRoomsLoading ||
                !roomTargetPropertyId ||
                !roomTargetType ||
                !roomTargetRoomId ||
                !roomReallocationReason.trim() ||
                (roomReallocationPeriod === "partial" &&
                  (!roomReallocationFrom ||
                    !roomReallocationTo ||
                    roomReallocationFrom > roomReallocationTo))
              }
              className="w-full bg-sky-700 text-white hover:bg-sky-800 sm:min-w-56 sm:w-auto"
            >
              {roomAssignmentSaving
                ? "Reallocating..."
                : "Confirm Room Reallocation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REALLOCATE ROOM / PROPERTY / NOT IN USE */}
      <Dialog
        open={!!reallocationTarget}
        onOpenChange={() => setReallocationTarget(null)}
      >
        <DialogContent className="bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-900">
              <RotateCcw className="h-5 w-5" /> Modify Booking for{" "}
              {reallocationTarget?.applicant.name}?
            </DialogTitle>
            <DialogDescription>
              Move between rooms, room types, Faiz properties or hotels. No
              charge is applied by default; the reason is always retained.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-sky-100 bg-sky-50/70 p-3 text-xs sm:grid-cols-4">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  TO Name
                </div>
                <div className="mt-0.5 font-semibold text-slate-900">
                  {applicantReallocationRequest?.requestedBy ||
                    reallocationTarget?.applicant.tourOperator ||
                    "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Serial No.
                </div>
                <div className="mt-0.5 font-semibold text-slate-900">
                  {applicantReallocationRequest?.id || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Tour Name
                </div>
                <div className="mt-0.5 font-semibold text-slate-900">
                  {applicantReallocationTour?.name ||
                    reallocationTarget?.applicant.tourName ||
                    "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Tour ID
                </div>
                <div className="mt-0.5 font-semibold text-slate-900">
                  {applicantReallocationTour?.id ||
                    reallocationTarget?.tourId ||
                    "—"}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-sky-100 bg-sky-50 p-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {reallocationTarget?.applicant.name}
                </div>
                <div className="text-xs text-slate-500">
                  ITS {reallocationTarget?.applicant.itsId}
                </div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">
                Current room{" "}
                {reallocationTarget?.applicant.roomAssigned || "Not assigned"}
              </Badge>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-semibold text-slate-900">
                    Requested stay
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {applicantReallocationRequest?.checkInDate || "—"} to{" "}
                    {applicantReallocationRequest?.checkOutDate || "—"}
                  </div>
                </div>
                <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => setReallocationPeriod("all")}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium ${reallocationPeriod === "all" ? "bg-sky-600 text-white" : "text-slate-600"}`}
                  >
                    All days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReallocationPeriod("partial");
                      setReallocationFrom(
                        applicantReallocationRequest?.checkInDate || "",
                      );
                      setReallocationTo(
                        applicantReallocationRequest?.checkOutDate || "",
                      );
                    }}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium ${reallocationPeriod === "partial" ? "bg-sky-600 text-white" : "text-slate-600"}`}
                  >
                    Partial days
                  </button>
                </div>
              </div>
              {reallocationPeriod === "partial" && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-[10px] font-medium text-slate-600">
                      Change room from
                    </div>
                    <Input
                      type="date"
                      min={applicantReallocationRequest?.checkInDate}
                      max={applicantReallocationRequest?.checkOutDate}
                      value={reallocationFrom}
                      onChange={(event) =>
                        setReallocationFrom(event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-[10px] font-medium text-slate-600">
                      Change room until
                    </div>
                    <Input
                      type="date"
                      min={
                        reallocationFrom ||
                        applicantReallocationRequest?.checkInDate
                      }
                      max={applicantReallocationRequest?.checkOutDate}
                      value={reallocationTo}
                      onChange={(event) =>
                        setReallocationTo(event.target.value)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-medium text-slate-600">
                  Target Property *
                </div>
                <Select
                  value={targetPropertyId}
                  onValueChange={(value) => {
                    setTargetPropertyId(value);
                    setTargetRoomId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id}>
                        {property.name} · {property.location}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-slate-600">
                  Target Room *
                </div>
                <Select value={targetRoomId} onValueChange={setTargetRoomId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select available room" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTargetRooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedTargetRoom && (
              <div className="grid grid-cols-3 gap-2 rounded-lg border border-violet-100 bg-violet-50 p-3 text-center">
                <div>
                  <div className="text-[10px] text-slate-500">Nightly Rate</div>
                  <div className="text-sm font-semibold">
                    USD {selectedTargetRoom.nightlyRate}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">
                    Available Beds
                  </div>
                  <div className="text-sm font-semibold">
                    {selectedTargetRoom.capacity - selectedTargetRoom.occupied}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Room Type</div>
                  <div className="text-sm font-semibold">
                    {standardRoomType(selectedTargetRoom.capacity)}
                  </div>
                </div>
              </div>
            )}
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold text-amber-900">
                    Optional financial adjustment
                  </div>
                  <div className="text-[10px] text-amber-700">
                    Keep zero and None for a normal operational move.
                  </div>
                </div>
                <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
                  Default: No charge
                </Badge>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <div className="mb-1 text-xs font-medium text-slate-600">
                    Additional Charge
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={reallocationCharge}
                    onChange={(event) =>
                      setReallocationCharge(Number(event.target.value))
                    }
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-slate-600">
                    Discount Type
                  </div>
                  <Select
                    value={reallocationDiscountType}
                    onValueChange={setReallocationDiscountType}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="its-category">ITS Category</SelectItem>
                      <SelectItem value="miqaat-policy">
                        Miqaat Policy
                      </SelectItem>
                      <SelectItem value="manager-discretion">
                        Manager Discretion
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-slate-600">
                    Discount Amount
                  </div>
                  <Input
                    type="number"
                    min="0"
                    disabled={reallocationDiscountType === "none"}
                    value={reallocationDiscount}
                    onChange={(event) =>
                      setReallocationDiscount(Number(event.target.value))
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-slate-600">
                Reason for Reallocation *
              </div>
              <TextareaUI
                value={reallocationReason}
                onChange={(event) => setReallocationReason(event.target.value)}
                placeholder="Explain why the room or property is being changed..."
                className="min-h-20"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReallocationTarget(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveReallocation}
              className="bg-violet-600 text-white hover:bg-violet-700"
            >
              Confirm Reallocation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* NOT IN USE */}

      {/* CONFIRM AUTO-ALLOCATION DIALOG */}
      <ConfirmDialog
        show={false}
        open={autoAllocationTarget !== null}
        onOpenChange={handleAutoAllocationDialogChange}
        title="Confirm Automatic Room Allocation"
        description="The system will validate and automatically assign available property rooms to every room allocation in the selected tour."
        confirmText="Auto-allocate rooms"
        cancelText="Cancel"
        confirmVariant="default"
        loading={roomAssignmentSaving}
        onConfirm={confirmAutoAllocation}
        onCancel={() => setAutoAllocationTarget(null)}
      >
        {autoAllocationTarget && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-sky-700">
              Selected tour
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {autoAllocationTarget.tourName}
            </div>
            <div className="mt-1 break-all text-xs text-slate-600">
              {autoAllocationTarget.tourCode}
            </div>
          </div>
        )}
      </ConfirmDialog>
      {/* CONFIRM AUTO-ALLOCATION DIALOG */}

      {/* CONFIRM ROOM UNALLOCATION DIALOG */}
      <ConfirmDialog
        open={roomUnallocationTarget !== null}
        onOpenChange={(open) => {
          if (!open && !roomUnallocationSaving) {
            setRoomUnallocationTarget(null);
          }
        }}
        title="Unallocate room?"
        description={`This will remove room ${roomUnallocationTarget?.assignedRoomNumber || "—"} from the selected logical room allocation. The room request and its applicants will remain unchanged.`}
        confirmText="Remove assignment"
        cancelText="Cancel"
        confirmVariant="destructive"
        loading={roomUnallocationSaving}
        onConfirm={confirmRoomUnallocation}
        onCancel={() => setRoomUnallocationTarget(null)}
      >
        {roomUnallocationTarget && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-red-700">
              Selected
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {roomUnallocationTarget.request.venue} ·{" "}
              {roomUnallocationTarget.request.roomType}
            </div>
            <div className="mt-1 text-xs text-slate-600">
              room: {roomUnallocationTarget.assignedRoomNumber || "—"}
            </div>
          </div>
        )}
      </ConfirmDialog>
      {/* CONFIRM ROOM UNALLOCATION DIALOG */}

      {/* REALLOCATE APPLICANT / NOT IN USE*/}
      <Dialog
        open={!!deallocationTarget}
        onOpenChange={() => setDeallocationTarget(null)}
      >
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <UserMinus className="h-5 w-5" /> Deallocate Applicant?
            </DialogTitle>
            <DialogDescription>
              Do you want to remove {deallocationTarget?.applicant.name} from
              room {deallocationTarget?.applicant.roomAssigned}? This applicant
              will remain visible as awaiting room allocation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="mb-1 text-xs font-medium text-slate-700">
              Reason for Deallocation *
            </div>
            <TextareaUI
              value={deallocationReason}
              onChange={(event) => setDeallocationReason(event.target.value)}
              placeholder="Enter the specific reason for removing this room allocation..."
              className="min-h-24"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeallocationTarget(null)}
            >
              Keep Allocation
            </Button>
            <Button
              onClick={saveDeallocation}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Confirm Deallocation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* NOT IN USE */}
    </div>
  );
};
